import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE_URL, apiGet, apiPost, apiPut, apiDelete, fetchAllPaginated } from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import ExportarRegistroAulasModal from '../components/ExportarRegistroAulasModal';
import RegistroHabilidades from '../components/RegistroHabilidades';
import { getFeriadoNacional } from '../utils/feriados';
import { hojeLocal } from '../utils/datas';
import Frequencia from './Frequencia';
import { alunoEstaNaTurma } from '../utils/statusAluno';
import {
  aulaResolvida, aulaSemConteudo, aulasParaOSeletor, conteudoAutomatico, outrasAulasDoBloco,
  rotuloDaAula,
} from '../utils/aulasDoDia';
import { ehInfantil, serieDeHabilidades } from '../utils/habilidades';

function DiarioTurma() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  // ============ ESTADOS PRINCIPAIS ============
  const [turma, setTurma] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const [subjects, setSubjects] = useState([]);
  // Quem leciona cada disciplina nesta turma (tb_class_subject). É o que a
  // tela mostra ao lado do seletor, para a coordenação saber de quem é o
  // diário que está olhando.
  const [atribuicoes, setAtribuicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Aba ativa. Aceita ?tab=... na URL para que os atalhos de Turmas (e a rota
  // antiga /turmas/:id/frequencia) caiam direto na aba certa. `chamada` era a
  // aba "Chamada do Dia", que virou a própria aba Frequência: links e
  // favoritos antigos continuam caindo nela.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabDaUrl = searchParams.get('tab') === 'chamada' ? 'frequencia' : searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    ['registro', 'frequencia', 'planilha', 'notas'].includes(tabDaUrl)
      ? tabDaUrl
      : 'registro'
  );

  // Aula em que a aba Frequência abre. Vem da URL (`?data=&aula=`, da Grade
  // Horária) ou do Registro de Aulas, logo depois de registrar uma aula.
  const [aulaParaChamada, setAulaParaChamada] = useState(() => {
    const data = searchParams.get('data');
    return data ? { data, periodo: searchParams.get('aula') } : null;
  });

  // Aula em que o Registro de Aulas abre (`?data=&disciplina=`, da
  // Auditoria): a disciplina, o mês e o dia dela. Vale só para a abertura.
  const aulaDaUrlRef = React.useRef(
    activeTab === 'registro' && /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('data') || '')
      ? { data: searchParams.get('data'), disciplina: searchParams.get('disciplina') }
      : null
  );

  // A fita de abas rola na horizontal no celular; a aba ativa precisa se
  // mostrar sozinha, senão quem entra por um link com ?tab= vê a fita
  // começando na primeira aba e conclui que o link não funcionou.
  const fitaAbasRef = React.useRef(null);
  useEffect(() => {
    const fita = fitaAbasRef.current;
    if (!fita) return undefined;
    // `scrollIntoView` não move um contêiner rolável de forma confiável
    // aqui (e pode arrastar a página junto), então a posição é calculada:
    // centraliza a aba ativa dentro da fita, sem tocar na rolagem da página.
    const ajustar = () => {
      const botao = fita.querySelector('[data-aba-ativa="true"]');
      if (!botao) return;
      const alvo = botao.offsetLeft - (fita.clientWidth - botao.offsetWidth) / 2;
      fita.scrollLeft = Math.max(0, alvo);
    };
    // Um quadro de espera: na primeira renderização as larguras da fita
    // ainda não estão resolvidas, e a conta daria zero.
    const id = requestAnimationFrame(ajustar);
    return () => cancelAnimationFrame(id);
    // `loading` entra aqui porque a tela mostra um spinner no lugar das abas
    // enquanto carrega: sem isto o ajuste rodava com a fita ainda fora do
    // DOM e nunca mais, deixando a aba do link fora de vista.
  }, [activeTab, loading]);

  // Mantém a URL em sincronia com a aba, para o link ser compartilhável e o
  // botão voltar do navegador funcionar entre abas.
  const handleSelectTab = (tabId) => {
    // A aula indicada vale para a abertura da Frequência; saindo da aba,
    // ela não deve reabrir na mesma aula da próxima vez.
    if (tabId !== 'frequencia') setAulaParaChamada(null);
    setActiveTab(tabId);
    setSearchParams(tabId === 'registro' ? {} : { tab: tabId }, { replace: true });
  };

  // ============ ESTADOS DO REGISTRO DE AULAS ============
  const [selectedSubject, setSelectedSubject] = useState('');
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [showEntryForm, setShowEntryForm] = useState(false);
  // Dia escolhido no calendário que já tem aula: a lista mostra só as aulas
  // dele. Antes o clique abria um formulário em branco no lugar da lista, e a
  // aula já registrada sumia da vista — o professor só a "via" pelo erro de
  // aula repetida, sem ter como abri-la.
  const [diaFiltrado, setDiaFiltrado] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  // Aulas previstas na grade horária para a data escolhida. É o que amarra o
  // registro a um horário específico — sem isso a frequência voltaria a valer
  // para o dia inteiro.
  const [daySlots, setDaySlots] = useState([]);
  const [loadingDaySlots, setLoadingDaySlots] = useState(false);
  const [entryForm, setEntryForm] = useState({
    lesson_date: hojeLocal(),
    class_period: '',
    // Polivalente: o componente trabalhado (Língua Portuguesa, Matemática...).
    id_componente: '',
    not_held_reason: '',
    lesson_type: 'normal',
    start_time: '',
    end_time: '',
    content_description: '',
    bncc_skills: '',
    observations: '',
    // Outras aulas do mesmo bloco (aulas geminadas) que recebem este mesmo
    // registro. Só no registro novo.
    aulas_geminadas: [],
  });
  const [savingEntry, setSavingEntry] = useState(false);
  const [entryError, setEntryError] = useState(null);
  const [entrySuccess, setEntrySuccess] = useState(false);

  // ============ ESTADOS DO CALENDÁRIO ============
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarDates, setCalendarDates] = useState({ all_dates: [], completed_dates: [], attendance_dates: [] });

  // ============ ESTADOS DA PLANILHA ============
  const [spreadsheetData, setSpreadsheetData] = useState(null);
  const [loadingSpreadsheet, setLoadingSpreadsheet] = useState(false);
  const [spreadsheetMonth, setSpreadsheetMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );
  const [spreadsheetChanges, setSpreadsheetChanges] = useState({});
  const [savingSpreadsheet, setSavingSpreadsheet] = useState(false);

  // ============ ESTADOS DE TEMPLATE/IMPORTAÇÃO ============
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importingFile, setImportingFile] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // A importação é um processo de três passos (baixar, preencher, enviar) e o
  // botão sozinho abria o seletor de arquivo direto — quem nunca tinha baixado
  // a planilha não fazia ideia de qual arquivo mandar.
  const [mostrarComoImportar, setMostrarComoImportar] = useState(false);
  const [exportandoRegistro, setExportandoRegistro] = useState(false);
  // Registro de aulas por planilha: o professor baixa a planilha da turma
  // (com as aulas já registradas), preenche uma linha por aula e devolve.
  const [baixandoModeloAulas, setBaixandoModeloAulas] = useState(false);
  const [importandoAulas, setImportandoAulas] = useState(false);
  const [resultadoAulas, setResultadoAulas] = useState(null);
  const [mostrarComoImportarAulas, setMostrarComoImportarAulas] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null); // 'registro' | 'notas' | 'planilha' | null

  // ============ CONSTANTES ============
  const attendanceStatusOptions = [
    { value: 'presente', label: 'Presente', color: 'green', short: 'P' },
    { value: 'ausente', label: 'Ausente', color: 'red', short: 'F' },
    { value: 'justificado', label: 'Justificado', color: 'yellow', short: 'J' },
    { value: 'atrasado', label: 'Atrasado', color: 'blue', short: 'A' },
  ];

  const lessonTypeOptions = [
    { value: 'normal', label: 'Aula' },
    { value: 'reposicao', label: 'Reposição' },
    // O nome diz as duas coisas porque é o mesmo caso: a aula que não é a
    // dele naquele horário. É este tipo que abre os horários dos colegas no
    // seletor de aula.
    { value: 'extra', label: 'Extra / Substituição' },
    { value: 'laboratorio', label: 'Laboratório' },
    // A aula estava na grade e não aconteceu. Registrar isso tira o horário
    // da cobrança da coordenação sem fingir que a aula foi dada.
    { value: 'nao_realizada', label: 'Aula não realizada' },
  ];

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  // Apenas Admin e Professor podem editar o diário (registro de aulas,
  // frequência e notas). Demais perfis têm acesso somente leitura/download.
  const canEdit = [ROLES.ADMIN, ROLES.PROFESSOR].includes(user?.role);


  // 1º e 2º ano e Educação Infantil não lançam nota: a aba Notas vira o
  // Registro de Habilidades (um conceito por habilidade em cada bimestre).
  // Mesmo id de aba, para os links com ?tab=notas continuarem caindo nela.
  const serieHabilidades = serieDeHabilidades(turma?.grade);
  // Na Educação Infantil a matriz é da turma, por Campo de Experiência: não
  // há componente do Polivalente a escolher.
  const matrizInfantil = ehInfantil(serieHabilidades);

  const tabs = [
    { id: 'registro', label: 'Registro de Aulas', icon: '📝' },
    { id: 'frequencia', label: 'Frequência', icon: '📅' },
    { id: 'planilha', label: 'Planilha', icon: '📊' },
    serieHabilidades
      ? { id: 'notas', label: 'Habilidades', icon: '🧩' }
      : { id: 'notas', label: 'Notas', icon: '🎯' },
  ];

  // ============ ESTADOS DE NOTAS ============
  // Estrutura local: { [studentId]: { b1: [n1, n2, n3], b2: [...], b3: [...], b4: [...], final: number } }
  // Persistido no backend via /diary-grades/ (uma linha por aluno+disciplina+ano).
  const [notasData, setNotasData] = useState({});
  // Ano letivo DA TURMA, não o ano do relógio. Uma turma de 2025 aberta em
  // 2026 lia e gravava as notas em 2026: a professora via o boletim em branco
  // e cada nota digitada criava uma linha num ano que não é o da turma —
  // invisível na tela do ano certo. `setTurma` corrige assim que a turma
  // carrega; o ano corrente é só o palpite enquanto ela não chegou.
  const [notasYear, setNotasYear] = useState(new Date().getFullYear());
  const [notasSaving, setNotasSaving] = useState({});
  // Erro de gravação por aluno. A tela salva sozinha, sem botão: se a falha
  // só aparecer no console, a professora acha que lançou a nota e ela não
  // está lá.
  const [notasErro, setNotasErro] = useState({});
  // Planilha de notas em Excel: o mesmo vaivém da aba Planilha (baixar,
  // preencher, importar). `notasRecarga` relê o boletim depois da importação.
  const [baixandoNotasExcel, setBaixandoNotasExcel] = useState(false);
  const [importandoNotas, setImportandoNotas] = useState(false);
  const [resultadoNotas, setResultadoNotas] = useState(null);
  const [mostrarComoImportarNotas, setMostrarComoImportarNotas] = useState(false);
  const [notasRecarga, setNotasRecarga] = useState(0);

  // Mapa do flat backend → estrutura local agrupada por bimestre.
  const fromBackendRow = (row) => ({
    b1: [row.b1_n1 ?? '', row.b1_n2 ?? '', row.b1_n3 ?? ''],
    b2: [row.b2_n1 ?? '', row.b2_n2 ?? '', row.b2_n3 ?? ''],
    b3: [row.b3_n1 ?? '', row.b3_n2 ?? '', row.b3_n3 ?? ''],
    b4: [row.b4_n1 ?? '', row.b4_n2 ?? '', row.b4_n3 ?? ''],
    b1r: [row.b1_rp1 ?? '', row.b1_rp2 ?? '', row.b1_rp3 ?? ''],
    b2r: [row.b2_rp1 ?? '', row.b2_rp2 ?? '', row.b2_rp3 ?? ''],
    b3r: [row.b3_rp1 ?? '', row.b3_rp2 ?? '', row.b3_rp3 ?? ''],
    b4r: [row.b4_rp1 ?? '', row.b4_rp2 ?? '', row.b4_rp3 ?? ''],
    final: row.final_exam ?? '',
  });

  const toBackendPayload = (s) => ({
    b1_n1: s.b1[0] === '' ? null : s.b1[0],
    b1_n2: s.b1[1] === '' ? null : s.b1[1],
    b1_n3: s.b1[2] === '' ? null : s.b1[2],
    b2_n1: s.b2[0] === '' ? null : s.b2[0],
    b2_n2: s.b2[1] === '' ? null : s.b2[1],
    b2_n3: s.b2[2] === '' ? null : s.b2[2],
    b3_n1: s.b3[0] === '' ? null : s.b3[0],
    b3_n2: s.b3[1] === '' ? null : s.b3[1],
    b3_n3: s.b3[2] === '' ? null : s.b3[2],
    b4_n1: s.b4[0] === '' ? null : s.b4[0],
    b4_n2: s.b4[1] === '' ? null : s.b4[1],
    b4_n3: s.b4[2] === '' ? null : s.b4[2],
    b1_rp1: s.b1r[0] === '' ? null : s.b1r[0],
    b1_rp2: s.b1r[1] === '' ? null : s.b1r[1],
    b1_rp3: s.b1r[2] === '' ? null : s.b1r[2],
    b2_rp1: s.b2r[0] === '' ? null : s.b2r[0],
    b2_rp2: s.b2r[1] === '' ? null : s.b2r[1],
    b2_rp3: s.b2r[2] === '' ? null : s.b2r[2],
    b3_rp1: s.b3r[0] === '' ? null : s.b3r[0],
    b3_rp2: s.b3r[1] === '' ? null : s.b3r[1],
    b3_rp3: s.b3r[2] === '' ? null : s.b3r[2],
    b4_rp1: s.b4r[0] === '' ? null : s.b4r[0],
    b4_rp2: s.b4r[1] === '' ? null : s.b4r[1],
    b4_rp3: s.b4r[2] === '' ? null : s.b4r[2],
    final_exam: s.final === '' ? null : s.final,
  });

  // Polivalente: a nota é lançada por componente (Português, Matemática...),
  // não numa disciplina "Polivalente". Ver backend/api/polivalente.py. No
  // Registro de Aulas, a aula fica no Polivalente e o componente a acompanha.
  const [componentesPoli, setComponentesPoli] = useState({ polivalente: null, componentes: [] });
  const [componenteNota, setComponenteNota] = useState('');

  useEffect(() => {
    if (activeTab !== 'notas' && activeTab !== 'registro') return;
    let cancelled = false;
    apiGet('/diary-grades/componentes/', { id_class: id })
      .then((resp) => {
        if (cancelled) return;
        setComponentesPoli(resp || { polivalente: null, componentes: [] });
        // A coordenação pode ter tirado da lista a disciplina que estava aberta.
        setComponenteNota((atual) => {
          const ids = (resp?.componentes || []).map((c) => String(c.id));
          return ids.includes(atual) ? atual : (ids[0] || '');
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeTab, id]);

  const ehPolivalente = !!componentesPoli.polivalente
    && String(selectedSubject) === String(componentesPoli.polivalente)
    && componentesPoli.componentes.length > 0;
  const disciplinaDaNota = ehPolivalente ? componenteNota : selectedSubject;

  // Carrega notas do backend ao abrir a aba ou trocar disciplina/ano.
  // No 1º e 2º ano e na Educação Infantil quem carrega é o RegistroHabilidades.
  useEffect(() => {
    if (activeTab !== 'notas' || !disciplinaDaNota || serieHabilidades) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await apiGet('/diary-grades/by_class/', {
          id_class: id,
          id_subject: disciplinaDaNota,
          school_year: notasYear,
        });
        if (cancelled) return;
        const next = {};
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          next[row.id_student] = fromBackendRow(row);
        });
        setNotasData(next);
      } catch (err) {
        console.error('Erro ao carregar notas:', err);
        if (!cancelled) setNotasData({});
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, id, disciplinaDaNota, notasYear, serieHabilidades, notasRecarga]);

  // Debounce por aluno: chama upsert apenas após 600ms de inatividade.
  const notaTimers = React.useRef({});
  const scheduleSave = useCallback((studentId, snapshot) => {
    if (!canEdit || !disciplinaDaNota) return;
    if (notaTimers.current[studentId]) clearTimeout(notaTimers.current[studentId]);
    notaTimers.current[studentId] = setTimeout(async () => {
      setNotasSaving(prev => ({ ...prev, [studentId]: true }));
      try {
        await apiPost('/diary-grades/upsert/', {
          id_class: id,
          id_student: studentId,
          id_subject: disciplinaDaNota,
          school_year: notasYear,
          ...toBackendPayload(snapshot),
        });
        setNotasErro(prev => {
          const { [studentId]: _ok, ...rest } = prev;
          return rest;
        });
      } catch (err) {
        console.error('Erro ao salvar nota:', err);
        // O servidor devolve a célula e o motivo ("2º bimestre · RP3:
        // precisa estar entre 0,0 e 10,0."). É o que a pessoa precisa ler
        // para saber o que corrigir nesta linha.
        const detalhe = Object.values(err?.response?.data || {})
          .filter(v => typeof v === 'string')
          .join(' ');
        setNotasErro(prev => ({
          ...prev,
          [studentId]: detalhe || 'Não foi possível salvar. Tente de novo.',
        }));
      } finally {
        setNotasSaving(prev => {
          const { [studentId]: _ignored, ...rest } = prev;
          return rest;
        });
      }
    }, 600);
  }, [canEdit, id, disciplinaDaNota, notasYear]);

  const getStudentNotas = (studentId) => {
    const s = notasData[studentId] || {};
    return {
      b1: s.b1 || ['', '', ''],
      b2: s.b2 || ['', '', ''],
      b3: s.b3 || ['', '', ''],
      b4: s.b4 || ['', '', ''],
      b1r: s.b1r || ['', '', ''],
      b2r: s.b2r || ['', '', ''],
      b3r: s.b3r || ['', '', ''],
      b4r: s.b4r || ['', '', ''],
      final: s.final ?? '',
    };
  };

  // A nota vai de 0 a 10. O `max` do <input type="number"> não impede digitar
  // 15: ele só reprova na validação de um formulário, e esta tela salva
  // sozinha, sem submit. Sem esta guarda um "15" digitado por engano vira
  // média final acima de 10 e reaparece no Painel da Rede como a melhor
  // turma da rede — foi o que aconteceu com a média 14,9.
  //
  // O limite de verdade é do servidor (`validar_nota`, em students/models.py):
  // aqui ele só evita a ida e volta e o susto do erro. Notas antigas fora da
  // escala continuam no banco e aparecem marcadas na tabela — ver
  // `notaForaDaEscala` no corpo da linha.
  const NOTA_MAXIMA = 10;
  const notaForaDaEscala = (value) => {
    if (value === '' || value === null || value === undefined) return false;
    const n = Number(value);
    return !Number.isNaN(n) && (n < 0 || n > NOTA_MAXIMA);
  };

  const handleNotaChange = (studentId, bimestre, idx, value) => {
    if (notaForaDaEscala(value)) return;
    const current = getStudentNotas(studentId);
    const updated = { ...current, [bimestre]: [...current[bimestre]] };
    updated[bimestre][idx] = value;
    setNotasData(prev => ({ ...prev, [studentId]: updated }));
    scheduleSave(studentId, updated);
  };

  // Recuperacao: RPi recupera a NTi do mesmo bimestre. A chave e o bimestre +
  // 'r' (ex.: 'b1r'), espelhando a estrutura de getStudentNotas.
  const handleRecupChange = (studentId, bimestreKey, idx, value) => {
    if (notaForaDaEscala(value)) return;
    const current = getStudentNotas(studentId);
    const updated = { ...current, [bimestreKey]: [...current[bimestreKey]] };
    updated[bimestreKey][idx] = value;
    setNotasData(prev => ({ ...prev, [studentId]: updated }));
    scheduleSave(studentId, updated);
  };

  const handleProvaFinalChange = (studentId, value) => {
    if (notaForaDaEscala(value)) return;
    const current = getStudentNotas(studentId);
    const updated = { ...current, final: value };
    setNotasData(prev => ({ ...prev, [studentId]: updated }));
    scheduleSave(studentId, updated);
  };

  // Media do bimestre: cada nota vale a maior entre a avaliacao (NTi) e a sua
  // recuperacao (RPi); tira a media dos slots que tem algum valor.
  const calcMediaBimestre = (notas, recups = ['', '', '']) => {
    const efetivas = [];
    for (let i = 0; i < 3; i++) {
      const nt = parseFloat(notas[i]);
      const rp = parseFloat(recups[i]);
      const vals = [nt, rp].filter(v => !isNaN(v));
      if (vals.length) efetivas.push(Math.max(...vals));
    }
    if (efetivas.length === 0) return null;
    return efetivas.reduce((a, b) => a + b, 0) / efetivas.length;
  };

  const calcMediaAnual = (s) => {
    const medias = ['b1', 'b2', 'b3', 'b4']
      .map(b => calcMediaBimestre(s[b], s[`${b}r`]))
      .filter(m => m !== null);
    if (medias.length === 0) return null;
    return medias.reduce((a, b) => a + b, 0) / medias.length;
  };

  const calcMediaFinal = (s) => {
    const mediaAnual = calcMediaAnual(s);
    const final = parseFloat(s.final);
    if (mediaAnual === null) return null;
    if (isNaN(final)) return mediaAnual;
    return (mediaAnual + final) / 2;
  };

  const formatNota = (n) => (n === null || n === undefined ? '-' : n.toFixed(1));
  const notaColor = (n) => {
    if (n === null || n === undefined) return darkMode ? 'text-slate-400' : 'text-slate-500';
    if (n >= 7) return 'text-[#13983D]';
    if (n >= 5) return 'text-[#F9B047]';
    return 'text-[#E83A3C]';
  };

  // ============ BUSCAR DADOS INICIAIS ============
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [turmaData, alunosData, subjectsData, atribuicoesData] = await Promise.all([
        apiGet(`/classes/${id}/`),
        // id_class no servidor: a tela baixava TODOS os alunos da rede para
        // ficar com os 30 desta turma — no celular do professor isso é o
        // carregamento inteiro.
        fetchAllPaginated('/students/', { id_class: id }),
        // id_class: backend restringe as disciplinas às que o professor
        // leciona nessa turma. Admin/Coordenador continuam vendo todas.
        fetchAllPaginated('/subjects/', { id_class: id }),
        // Sem a lista (perfil sem acesso a ela), a tela segue sem o nome.
        apiGet('/class-subjects/by-class/', { id_class: id }).catch(() => []),
      ]);

      setTurma(turmaData);
      setAtribuicoes(Array.isArray(atribuicoesData) ? atribuicoesData : (atribuicoesData?.results || []));
      if (turmaData?.school_year) setNotasYear(turmaData.school_year);
      const turmaAlunos = alunosData
        // Quem foi transferido ou desistiu sai da caderneta. A regra é a
        // mesma do backend (api/alunos_turma.py).
        .filter(alunoEstaNaTurma)
        // Ordem alfabética: agiliza frequência e lançamento de notas, e casa com
        // as planilhas que as professoras já usam.
        .sort((a, b) => (a.student_name || '').localeCompare(b.student_name || '', 'pt-BR'));
      setAlunos(turmaAlunos);
      setSubjects(subjectsData);

      if (subjectsData.length > 0) {
        // Abre na disciplina da aula mais recente, e no mês dela. Abrir na
        // primeira em ordem alfabética deixava a turma de Polivalente em
        // Artes, vazia: o painel dizia "226 aulas" e o diário, "nenhum
        // registro de aula para este mês".
        let inicial = subjectsData[0].id;
        try {
          const ultima = await apiGet('/diary-entries/ultima_aula/', { id_class: id });
          if (ultima?.id_subject && subjectsData.some(s => String(s.id) === String(ultima.id_subject))) {
            inicial = ultima.id_subject;
            if (ultima.lesson_date) {
              setCalendarMonth(Number(ultima.lesson_date.slice(5, 7)) - 1);
              setCalendarYear(Number(ultima.lesson_date.slice(0, 4)));
            }
          }
        } catch (e) {
          // Sem a última aula, fica a primeira disciplina — como antes.
        }
        const pedida = aulaDaUrlRef.current;
        if (pedida) {
          const disciplina = subjectsData.find(s => String(s.id) === String(pedida.disciplina));
          if (disciplina) inicial = disciplina.id;
          setCalendarMonth(Number(pedida.data.slice(5, 7)) - 1);
          setCalendarYear(Number(pedida.data.slice(0, 4)));
        }
        setSelectedSubject(inicial);
      }
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados da turma.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============ BUSCAR REGISTROS DE AULA ============
  const fetchEntries = useCallback(async () => {
    if (!selectedSubject) return;
    setLoadingEntries(true);
    try {
      const month = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}`;
      const response = await apiGet('/diary-entries/by_class/', {
        id_class: id,
        id_subject: selectedSubject,
        month,
      });
      setDiaryEntries(response.results || []);
    } catch (err) {
      console.error('Erro ao buscar registros:', err);
      setDiaryEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  }, [id, selectedSubject, calendarMonth, calendarYear]);

  // `activeTab` entra para reler ao voltar ao Registro: a chamada é lançada
  // na aba Frequência, e sem reler o calendário e a lista seguiam mostrando a
  // aula sem frequência (laranja) até a página ser recarregada.
  useEffect(() => {
    if (selectedSubject && activeTab === 'registro') {
      fetchEntries();
      fetchCalendarDates();
    }
  }, [selectedSubject, calendarMonth, calendarYear, activeTab]);

  // Outro mês ou outra disciplina: o dia escolhido não vale mais — exceto o
  // da URL, quando a disciplina e o mês dele chegam.
  useEffect(() => {
    const pedida = aulaDaUrlRef.current;
    const mes = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}`;
    if (pedida && selectedSubject && pedida.data.startsWith(mes)) {
      aulaDaUrlRef.current = null;
      setDiaFiltrado(pedida.data);
      return;
    }
    setDiaFiltrado(null);
  }, [selectedSubject, calendarMonth, calendarYear]);

  // As aulas desta disciplina já registradas na data do formulário (a lista
  // do mês já está carregada). Ver o que existe antes de gravar é o que evita
  // o erro de aula repetida.
  const registradasNaData = React.useMemo(
    () => (entryForm.lesson_date
      ? diaryEntries.filter(
        e => e.lesson_date === entryForm.lesson_date && e.id !== editingEntry?.id
      )
      : []),
    [diaryEntries, entryForm.lesson_date, editingEntry]
  );

  // ============ BUSCAR DATAS DO CALENDÁRIO ============
  const fetchCalendarDates = async () => {
    try {
      const response = await apiGet('/diary-entries/calendar_dates/', {
        id_class: id,
        id_subject: selectedSubject || undefined,
        month: `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}`,
      });
      setCalendarDates({
        all_dates: response.all_dates || [],
        completed_dates: response.completed_dates || [],
        attendance_dates: response.attendance_dates || [],
      });
    } catch (err) {
      console.error('Erro ao buscar datas:', err);
    }
  };

  // ============ FUNÇÕES DO CALENDÁRIO ============
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const formatDateForComparison = (day) => {
    const month = String(calendarMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${calendarYear}-${month}-${dayStr}`;
  };

  // ============ REGISTRO DE AULA - FUNÇÕES ============
  const resetEntryForm = () => {
    setEntryForm({
      lesson_date: hojeLocal(),
      class_period: '',
      id_componente: '',
      not_held_reason: '',
      lesson_type: 'normal',
      start_time: '',
      end_time: '',
      content_description: '',
      bncc_skills: '',
      observations: '',
      aulas_geminadas: [],
    });
    setEditingEntry(null);
    setEntryError(null);
  };

  // Busca na grade horária quais aulas a turma tem na data do formulário.
  // O professor então escolhe QUAL aula está registrando.
  useEffect(() => {
    if (!showEntryForm || !entryForm.lesson_date) {
      setDaySlots([]);
      return;
    }
    let alive = true;
    (async () => {
      setLoadingDaySlots(true);
      try {
        const resp = await apiGet('/class-schedule/for_date/', {
          id_class: id,
          date: entryForm.lesson_date,
        });
        if (alive) setDaySlots(resp.lessons || []);
      } catch (err) {
        console.error('Erro ao buscar aulas do dia:', err);
        if (alive) setDaySlots([]);
      } finally {
        if (alive) setLoadingDaySlots(false);
      }
    })();
    return () => { alive = false; };
  }, [showEntryForm, entryForm.lesson_date, id]);

  // O professor da disciplina escolhida. Sem professor próprio, quem
  // responde pela disciplina é o regente da turma — a mesma regra que decide
  // quem pode lançar no diário (`get_taught_class_subject_pairs`).
  const professorDaDisciplina = React.useMemo(() => {
    if (!selectedSubject) return null;
    const nomes = [...new Set(
      atribuicoes
        .filter(a => String(a.id_subject) === String(selectedSubject) && a.teacher_name)
        .map(a => a.teacher_name)
    )];
    if (nomes.length) {
      return { rotulo: nomes.length > 1 ? 'Professores' : 'Professor(a)', nome: nomes.join(', ') };
    }
    if (turma?.teacher_name) return { rotulo: 'Regente da turma', nome: turma.teacher_name };
    return null;
  }, [atribuicoes, selectedSubject, turma]);

  // As opções do seletor "Aula do dia": as aulas DESTE professor na turma,
  // naquele dia. A regra (e o porquê da volta atrás) está em utils/aulasDoDia.
  // Coordenação passa `idTeacher` nulo e vê o dia inteiro — é o papel dela.
  const slotsDaDisciplina = React.useMemo(
    () => aulasParaOSeletor(daySlots, {
      idDisciplina: selectedSubject,
      idTeacher: user?.role === ROLES.PROFESSOR ? user?.id_teacher : null,
      // Os horários dos colegas só entram depois que ele diz que está
      // cobrindo alguém. No dia a dia ele lança a aula dele, e a lista
      // inteira do dia no meio do caminho seria ruído.
      incluirSubstituicoes: entryForm.lesson_type === 'extra',
    }),
    [daySlots, selectedSubject, user, entryForm.lesson_type]
  );

  // A aula escolhida, quando ela é o horário de um colega. É o que a tela usa
  // para avisar o que vai acontecer ao salvar.
  const aulaSubstituida = React.useMemo(
    () => slotsDaDisciplina.find(
      s => s.substituindo && String(s.period) === String(entryForm.class_period)
    ) || null,
    [slotsDaDisciplina, entryForm.class_period]
  );

  // A aula escolhida já está no diário (outro registro que não o que está
  // sendo editado). Gravar de novo só daria o erro de aula repetida: a tela
  // diz que ela já existe e leva até ela — ou até a chamada que falta.
  const aulaJaRegistrada = React.useMemo(
    () => (entryForm.class_period
      ? slotsDaDisciplina.find(
        s => s.registered
          && s.id_diary_entry
          && s.id_diary_entry !== editingEntry?.id
          && String(s.period) === String(entryForm.class_period)
      ) || null
      : null),
    [slotsDaDisciplina, entryForm.class_period, editingEntry]
  );

  // Aulas geminadas: as outras aulas dele na turma, nesse dia, que podem
  // receber este mesmo registro. Só no registro novo, e não na substituição
  // (cobrir um colega é uma aula só).
  const aulasDoBloco = React.useMemo(
    () => (editingEntry || aulaSubstituida
      ? []
      : outrasAulasDoBloco(slotsDaDisciplina, entryForm.class_period)),
    [slotsDaDisciplina, entryForm.class_period, editingEntry, aulaSubstituida]
  );
  // As marcadas que continuam valendo: trocar a aula do dia ou a data pode
  // tirar uma delas da lista.
  const geminadasMarcadas = entryForm.aulas_geminadas.filter(
    p => aulasDoBloco.some(s => String(s.period) === String(p))
  );

  // Turma sem grade: a aula é gravada sem período, e o backend recusa uma
  // segunda aula comum da disciplina no mesmo dia (reposição e aula extra
  // podem repetir). A tela avisa antes, em vez de deixar gravar e errar.
  const TIPOS_QUE_REPETEM = ['reposicao', 'extra'];
  const repeteAulaSemGrade = !aulaJaRegistrada
    && !entryForm.class_period
    && !TIPOS_QUE_REPETEM.includes(entryForm.lesson_type)
    && registradasNaData.some(
      e => !e.class_period && !TIPOS_QUE_REPETEM.includes(e.lesson_type)
    );

  // Leva à aba Frequência já na aula indicada.
  const abrirChamadaDaAula = ({ data, periodo, idAula }) => {
    setShowEntryForm(false);
    resetEntryForm();
    setAulaParaChamada({ data, periodo, idAula });
    handleSelectTab('frequencia');
  };

  const handleNewEntry = () => {
    resetEntryForm();
    setShowEntryForm(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      lesson_date: entry.lesson_date,
      class_period: entry.class_period ? String(entry.class_period) : '',
      id_componente: entry.id_componente ? String(entry.id_componente) : '',
      not_held_reason: entry.not_held_reason || '',
      lesson_type: entry.lesson_type,
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      // O texto automático da aula criada pela chamada não é conteúdo:
      // abre vazio para a professora escrever o que trabalhou.
      content_description: conteudoAutomatico(entry.content_description)
        ? '' : entry.content_description,
      bncc_skills: entry.bncc_skills || '',
      observations: entry.observations || '',
      aulas_geminadas: [],
    });
    setShowEntryForm(true);
  };

  const handleCalendarDayClick = (day) => {
    const dateStr = formatDateForComparison(day);
    // Dia com aula registrada: mostra o que já foi lançado nele. Registrar
    // outra aula no mesmo dia continua a um clique ("+ Nova aula neste dia").
    if (calendarDates.all_dates?.includes(dateStr)) {
      setShowEntryForm(false);
      resetEntryForm();
      setDiaFiltrado(dateStr);
      return;
    }
    if (!canEdit) return;
    const today = hojeLocal();
    if (dateStr > today) {
      setEntryError('Não é permitido registrar aulas em datas futuras.');
      return;
    }
    const feriado = getFeriadoNacional(dateStr);
    if (feriado) {
      setEntryError(`${feriado} é feriado nacional — não há aula nesta data.`);
      return;
    }
    abrirFormularioNoDia(dateStr);
  };

  const abrirFormularioNoDia = (dateStr) => {
    resetEntryForm();
    setEntryForm(prev => ({ ...prev, lesson_date: dateStr }));
    setShowEntryForm(true);
  };

  const handleSaveEntry = async () => {
    const naoRealizada = entryForm.lesson_type === 'nao_realizada';
    if (naoRealizada) {
      if (!entryForm.not_held_reason.trim()) {
        setEntryError('Informe o motivo de a aula não ter acontecido.');
        return;
      }
    } else {
      if (!entryForm.content_description.trim()) {
        setEntryError('O conteúdo ministrado e a metodologia são obrigatórios.');
        return;
      }
      if (!entryForm.bncc_skills.trim()) {
        setEntryError('As habilidades BNCC são obrigatórias.');
        return;
      }
    }
    if (!selectedSubject) {
      setEntryError('Selecione uma disciplina.');
      return;
    }
    const today = hojeLocal();
    if (entryForm.lesson_date > today) {
      setEntryError('Não é permitido registrar aulas em datas futuras.');
      return;
    }
    const feriadoDaAula = getFeriadoNacional(entryForm.lesson_date);
    if (feriadoDaAula) {
      setEntryError(`${feriadoDaAula} é feriado nacional — não há aula nesta data.`);
      return;
    }

    // Validação de horários: dentro do horário letivo (06:00–22:00) e fim > início.
    const SCHOOL_START = '06:00';
    const SCHOOL_END = '22:00';
    const { start_time: startTime, end_time: endTime } = entryForm;
    // O horário salvo volta do servidor com segundos ("22:00:00"), e
    // "22:00:00" > "22:00" na comparação de texto: a aula noturna que termina
    // às 22h não podia mais ser editada.
    for (const [label, valor] of [['início', startTime], ['fim', endTime]]) {
      const value = (valor || '').slice(0, 5);
      if (value && (value < SCHOOL_START || value > SCHOOL_END)) {
        setEntryError(`O horário de ${label} deve estar dentro do horário letivo (06:00 às 22:00).`);
        return;
      }
    }
    if (startTime && endTime && endTime.slice(0, 5) <= startTime.slice(0, 5)) {
      setEntryError('O horário de fim deve ser maior que o horário de início.');
      return;
    }

    setSavingEntry(true);
    setEntryError(null);
    setEntrySuccess(false);

    try {
      const payload = {
        id_class: parseInt(id),
        id_subject: parseInt(selectedSubject),
        ...entryForm,
        start_time: entryForm.start_time || null,
        end_time: entryForm.end_time || null,
        class_period: entryForm.class_period ? parseInt(entryForm.class_period, 10) : null,
        // Só a aula do Polivalente tem componente (o backend recusa nas demais).
        id_componente: ehPolivalente && entryForm.id_componente
          ? parseInt(entryForm.id_componente, 10) : null,
        bncc_skills: entryForm.bncc_skills || null,
        not_held_reason: naoRealizada ? entryForm.not_held_reason : null,
        observations: entryForm.observations || null,
        aulas_geminadas: !editingEntry && geminadasMarcadas.length
          ? geminadasMarcadas.map(p => parseInt(p, 10))
          : undefined,
      };

      let createdOrUpdated;
      if (editingEntry) {
        createdOrUpdated = await apiPut(`/diary-entries/${editingEntry.id}/`, payload);
      } else {
        createdOrUpdated = await apiPost('/diary-entries/', payload);
      }

      setEntrySuccess(true);
      setShowEntryForm(false);
      const wasNew = !editingEntry;
      resetEntryForm();
      // A lista e o calendário mostram um mês. Aula gravada em outro mês
      // (registro atrasado, data trocada no formulário) não aparecia na lista
      // depois de salvar — "a aula sumiu". O calendário vai até o mês dela.
      const mesDaAula = createdOrUpdated?.lesson_date
        ? {
          mes: Number(createdOrUpdated.lesson_date.slice(5, 7)) - 1,
          ano: Number(createdOrUpdated.lesson_date.slice(0, 4)),
        }
        : null;
      if (mesDaAula && (mesDaAula.mes !== calendarMonth || mesDaAula.ano !== calendarYear)) {
        // Trocar o mês já relê a lista e o calendário (efeito acima).
        setCalendarMonth(mesDaAula.mes);
        setCalendarYear(mesDaAula.ano);
      } else {
        await fetchEntries();
        await fetchCalendarDates();
      }

      // Ao criar uma nova aula, abre automaticamente a Frequência já com
      // essa aula selecionada para preenchimento imediato.
      // Aula que não aconteceu não tem chamada a lançar.
      if (wasNew && createdOrUpdated?.id && !naoRealizada) {
        setAulaParaChamada({
          data: createdOrUpdated.lesson_date,
          periodo: createdOrUpdated.class_period,
          idAula: createdOrUpdated.id,
          // As aulas geminadas gravadas junto: a chamada já abre marcada
          // para valer nelas também.
          geminadas: (createdOrUpdated.geminadas || []).map(g => g.id),
        });
        handleSelectTab('frequencia');
      }

      setTimeout(() => setEntrySuccess(false), 3000);
    } catch (err) {
      console.error('Erro ao salvar registro:', err);
      setEntryError(err.message || 'Erro ao salvar registro de aula.');
    } finally {
      setSavingEntry(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro de aula?')) return;
    try {
      await apiDelete(`/diary-entries/${entryId}/`);
      fetchEntries();
      fetchCalendarDates();
    } catch (err) {
      console.error('Erro ao excluir:', err);
    }
  };

  const handleToggleComplete = async (entryId) => {
    try {
      await apiPost(`/diary-entries/${entryId}/toggle_complete/`);
      fetchEntries();
      fetchCalendarDates();
    } catch (err) {
      console.error('Erro ao alterar status:', err);
    }
  };

  // ============ PLANILHA - FUNÇÕES ============
  const fetchSpreadsheet = async () => {
    setLoadingSpreadsheet(true);
    try {
      const response = await apiGet('/diary-attendance/spreadsheet/', {
        id_class: id,
        id_subject: selectedSubject || undefined,
        month: spreadsheetMonth,
      });
      setSpreadsheetData(response);
      setSpreadsheetChanges({});
    } catch (err) {
      console.error('Erro ao carregar planilha:', err);
      setSpreadsheetData(null);
    } finally {
      setLoadingSpreadsheet(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'planilha' && selectedSubject) {
      fetchSpreadsheet();
    }
  }, [activeTab, selectedSubject, spreadsheetMonth]);

  const handleSpreadsheetChange = (studentId, entryId, status) => {
    const key = `${studentId}-${entryId}`;
    setSpreadsheetChanges(prev => ({
      ...prev,
      [key]: { id_student: studentId, id_diary_entry: entryId, status },
    }));
  };

  const handleSaveSpreadsheet = async () => {
    const changes = Object.values(spreadsheetChanges);
    if (changes.length === 0) return;

    setSavingSpreadsheet(true);
    try {
      await apiPost('/diary-attendance/bulk_spreadsheet/', { data: changes });
      fetchSpreadsheet();
      fetchEntries();
      fetchCalendarDates();
    } catch (err) {
      console.error('Erro ao salvar planilha:', err);
    } finally {
      setSavingSpreadsheet(false);
    }
  };

  // ============ DOWNLOAD GENÉRICO DE PDF ============
  const downloadPdf = async (path, params, fallbackName, kind) => {
    setDownloadingPdf(kind);
    try {
      const qs = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE_URL}${path}?${qs}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao gerar PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '') || fallbackName
        : fallbackName;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
      alert(err.message || 'Não foi possível gerar o PDF.');
      return false;
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadNotasPdf = () => {
    if (!disciplinaDaNota) return;
    downloadPdf(
      '/diary-grades/pdf/',
      { id_class: id, id_subject: disciplinaDaNota, school_year: notasYear },
      `notas_${notasYear}.pdf`,
      'notas',
    );
  };

  const handleBaixarNotasExcel = async () => {
    if (!disciplinaDaNota) return;
    setBaixandoNotasExcel(true);
    try {
      const params = new URLSearchParams({
        id_class: id,
        id_subject: disciplinaDaNota,
        school_year: notasYear,
      });
      const response = await fetch(`${API_BASE_URL}/diary-grades/planilha/?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || 'Erro ao baixar a planilha');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers.get('Content-Disposition');
      const a = document.createElement('a');
      a.href = url;
      a.download = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `notas_${notasYear}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar planilha de notas:', err);
      setResultadoNotas({ success: false, error: err.message });
    } finally {
      setBaixandoNotasExcel(false);
    }
  };

  const handleImportarNotas = async (event) => {
    const file = event.target.files[0];
    if (!file || !disciplinaDaNota) return;
    setImportandoNotas(true);
    setResultadoNotas(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id_class', id);
      formData.append('id_subject', disciplinaDaNota);
      formData.append('school_year', notasYear);
      const response = await fetch(`${API_BASE_URL}/diary-grades/importar-planilha/`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Erro ao importar planilha');
      }
      setResultadoNotas(data);
      setNotasRecarga((n) => n + 1);
    } catch (err) {
      console.error('Erro ao importar notas:', err);
      setResultadoNotas({ success: false, error: err.message });
    } finally {
      setImportandoNotas(false);
      event.target.value = '';
    }
  };

  const handleBaixarModeloAulas = async () => {
    setBaixandoModeloAulas(true);
    try {
      const params = new URLSearchParams({ id_class: id });
      const response = await fetch(`${API_BASE_URL}/diary-entries/planilha-modelo/?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || 'Erro ao baixar o modelo');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers.get('Content-Disposition');
      const a = document.createElement('a');
      a.href = url;
      a.download = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : 'registro_aulas.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar modelo de registro:', err);
      setResultadoAulas({ success: false, error: err.message });
    } finally {
      setBaixandoModeloAulas(false);
    }
  };

  const handleImportarAulas = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImportandoAulas(true);
    setResultadoAulas(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id_class', id);
      const response = await fetch(`${API_BASE_URL}/diary-entries/importar-planilha/`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Erro ao importar planilha');
      }
      setResultadoAulas(data);
      fetchEntries();
      fetchCalendarDates();
    } catch (err) {
      console.error('Erro ao importar registro de aulas:', err);
      setResultadoAulas({ success: false, error: err.message });
    } finally {
      setImportandoAulas(false);
      event.target.value = '';
    }
  };

  const handleExportarRegistro = async (filtros) => {
    const baixou = await downloadPdf(
      '/diary-entries/pdf/',
      { id_class: id, ...filtros },
      'registro_aulas.pdf',
      'registro',
    );
    if (baixou) setExportandoRegistro(false);
  };

  const handleDownloadPlanilhaPdf = () => {
    const params = { id_class: id, month: spreadsheetMonth };
    if (selectedSubject) params.id_subject = selectedSubject;
    downloadPdf(
      '/diary-attendance/pdf/',
      params,
      `frequencia_${spreadsheetMonth}.pdf`,
      'planilha',
    );
  };

  // ============ TEMPLATE/IMPORTAÇÃO - FUNÇÕES ============
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      // API_BASE_URL imported from services/api
      const params = new URLSearchParams({
        id_class: id,
        month: spreadsheetMonth,
      });
      if (selectedSubject) params.append('id_subject', selectedSubject);

      const response = await fetch(`${API_BASE_URL}/diary-attendance/download_template/?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao baixar template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `frequencia_${spreadsheetMonth}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar template:', err);
      setImportResult({ success: false, error: err.message });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportingFile(true);
    setImportResult(null);

    try {
      // API_BASE_URL imported from services/api
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id_class', id);
      formData.append('month', spreadsheetMonth);
      if (selectedSubject) formData.append('id_subject', selectedSubject);

      const response = await fetch(`${API_BASE_URL}/diary-attendance/import_spreadsheet/`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao importar planilha');
      }

      setImportResult(data);
      fetchSpreadsheet();
      fetchEntries();
      fetchCalendarDates();
    } catch (err) {
      console.error('Erro ao importar:', err);
      setImportResult({ success: false, error: err.message });
    } finally {
      setImportingFile(false);
      event.target.value = '';
    }
  };

  /** '2026-09' -> 'setembro de 2026'. Ninguém escreve mês como "2026-09". */
  const mesPorExtenso = (ym) => {
    const [ano, mes] = String(ym || '').split('-');
    const nome = monthNames[Number(mes) - 1];
    return nome ? `${nome.toLowerCase()} de ${ano}` : ym;
  };

  // ============ RENDERIZAÇÃO ============

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Diário da Turma..."
        subtitle="Buscando aulas, frequência e notas..."
        size="xl"
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center max-w-md ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className="text-[#E83A3C] mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/turmas')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode
                ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              Voltar para Turmas
            </button>
            <button
              onClick={fetchData}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode
                ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
                : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20'
              }`}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/turmas')}
              className={`p-2 rounded-lg transition-colors ${darkMode
                ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
              }`}
              title="Voltar para Turmas"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-gray-50 to-gray-400'
              : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
              Diário da Turma
            </h1>
          </div>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {turma?.class_name} - {turma?.school_name}
          </p>
        </div>

        {/* Seletor de disciplina, com quem a leciona logo abaixo */}
        <div className="flex flex-col items-start sm:items-end gap-1.5">
          <div className="flex items-center gap-3">
            <label className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Disciplina:
            </label>
            <SearchableSelect
              value={selectedSubject}
              onChange={setSelectedSubject}
              options={subjects.map(s => ({ value: s.id, label: s.subject_name }))}
              placeholder="Selecione..."
              darkMode={darkMode}
              className="min-w-[200px]"
            />
          </div>
          {selectedSubject && (
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {professorDaDisciplina ? (
                <>
                  {professorDaDisciplina.rotulo}:{' '}
                  <span className={`font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {professorDaDisciplina.nome}
                  </span>
                </>
              ) : (
                'Sem professor vinculado a esta disciplina na turma'
              )}
            </p>
          )}
        </div>
      </header>

      {/* Mensagens de sucesso */}
      {entrySuccess && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
          ? 'bg-[#13983D]/20 text-[#13983D] border border-[#13983D]/30'
          : 'bg-[#13983D]/10 text-[#13983D] border border-[#13983D]/20'
        }`}>
          Registro de aula salvo com sucesso!
        </div>
      )}

      {/* Abas */}
      {/* No celular as cinco abas não cabem lado a lado: viram uma fita que
          rola na horizontal, cada uma com a sua largura. A partir de md
          voltam a dividir a linha igualmente (`flex-1`). */}
      <div
        ref={fitaAbasRef}
        className={`flex gap-1 mb-6 p-1 rounded-xl overflow-x-auto scrollbar-thin ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-aba-ativa={activeTab === tab.id}
            onClick={() => handleSelectTab(tab.id)}
            className={`flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 md:flex-1 md:flex-shrink justify-center ${
              activeTab === tab.id
                ? 'text-white shadow-lg'
                : darkMode
                  ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}
            style={activeTab === tab.id ? { background: 'linear-gradient(135deg, #0974BA, #2E4974)' } : {}}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ============ ABA: REGISTRO DE AULAS ============ */}
      {activeTab === 'registro' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendário */}
          <div className={`rounded-xl p-5 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Calendário de Aulas
            </h3>

            {/* Navegação do mês */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => {
                  if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(y => y - 1); }
                  else setCalendarMonth(m => m - 1);
                }}
                className={`p-2 rounded-lg transition-colors ${darkMode
                  ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                  : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className={`font-semibold text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                {monthNames[calendarMonth]} {calendarYear}
              </span>
              <button
                onClick={() => {
                  if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(y => y + 1); }
                  else setCalendarMonth(m => m + 1);
                }}
                className={`p-2 rounded-lg transition-colors ${darkMode
                  ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                  : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dias da semana */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className={`text-center text-xs font-semibold py-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {day}
                </div>
              ))}
            </div>

            {/* Dias do mês */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: getFirstDayOfMonth(calendarMonth, calendarYear) }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}
              {Array.from({ length: getDaysInMonth(calendarMonth, calendarYear) }).map((_, i) => {
                const day = i + 1;
                const dateStr = formatDateForComparison(day);
                const hasEntry = calendarDates.all_dates?.includes(dateStr);
                const isCompleted = calendarDates.completed_dates?.includes(dateStr);
                const isToday = day === new Date().getDate() && calendarMonth === new Date().getMonth() && calendarYear === new Date().getFullYear();
                const todayStr = hojeLocal();
                const isFuture = dateStr > todayStr;
                // Feriado nacional só bloqueia enquanto não houver aula
                // registrada nele — um lançamento antigo continua acessível.
                const feriado = !hasEntry ? getFeriadoNacional(dateStr) : null;
                const isBlocked = isFuture || !!feriado;

                return (
                  <button
                    key={day}
                    disabled={isBlocked}
                    onClick={() => !isBlocked && (canEdit || hasEntry) && handleCalendarDayClick(day)}
                    className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                      isFuture ? 'cursor-not-allowed opacity-40' : feriado ? 'cursor-not-allowed' : 'hover:scale-105'
                    } ${
                      diaFiltrado === dateStr
                        ? darkMode
                          ? 'ring-2 ring-offset-2 ring-slate-100 ring-offset-slate-800'
                          : 'ring-2 ring-offset-2 ring-[#2E4974] ring-offset-white'
                        : ''
                    } ${
                      feriado
                        ? darkMode
                          ? 'bg-slate-800/60 text-slate-600 line-through'
                          : 'bg-slate-50 text-slate-300 line-through'
                        : isCompleted
                        ? 'bg-[#13983D] text-white shadow-lg shadow-[#13983D]/30 hover:bg-[#13983D]/80'
                        : hasEntry
                        ? 'bg-[#F9B047] text-white shadow-lg shadow-[#F9B047]/30 hover:bg-[#F9B047]/80'
                        : isToday
                        ? darkMode
                          ? 'bg-[#0974BA]/30 text-[#0974BA] ring-2 ring-[#0974BA]'
                          : 'bg-[#0974BA]/10 text-[#0974BA] ring-2 ring-[#0974BA]'
                        : darkMode
                        ? 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    title={
                      feriado ? `Feriado nacional — ${feriado}`
                        : isFuture ? 'Não é permitido registrar aulas em datas futuras'
                        : isCompleted ? 'Aulas do dia registradas e com frequência — clique para ver'
                        : hasEntry ? 'Aula registrada com pendência (frequência ou conteúdo) — clique para ver'
                        : 'Clique para registrar aula'
                    }
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Legenda */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-4 pt-4 border-t border-slate-600/30">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#13983D]" />
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Concluída</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#F9B047]" />
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Falta frequência ou conteúdo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ring-2 ring-[#0974BA] ${darkMode ? 'bg-[#0974BA]/30' : 'bg-[#0974BA]/10'}`} />
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Hoje</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${darkMode ? 'bg-slate-800/60' : 'bg-slate-50 border border-slate-200'}`} />
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Feriado</span>
              </div>
            </div>

            {/* Info turma */}
            <div className="mt-6 pt-4 border-t border-slate-600/30">
              <div className="grid grid-cols-2 gap-3">
                <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                    {alunos.length}
                  </div>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Alunos</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                    {diaryEntries.length}
                  </div>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Aulas no mês</p>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Registros / Formulário */}
          <div className={`lg:col-span-2 rounded-xl p-5 ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h3 className={`text-sm font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {showEntryForm ? (editingEntry ? 'Editar Registro de Aula' : 'Novo Registro de Aula') : 'Registros de Aulas'}
              </h3>
              {!showEntryForm && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setExportandoRegistro(true)}
                    disabled={subjects.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      subjects.length === 0
                        ? 'bg-slate-500 text-white cursor-not-allowed'
                        : 'text-white bg-gradient-to-r from-[#E83A3C] to-[#C4292B] hover:shadow-lg hover:shadow-[#E83A3C]/25'
                    }`}
                    title="Escolha o bimestre, mês ou datas e gere o PDF para imprimir e assinar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Baixar PDF
                  </button>
                  {/* Planilha: abre o passo a passo (baixar o modelo,
                      preencher, enviar), como na aba Planilha. */}
                  {canEdit && (
                    <button
                      onClick={() => setMostrarComoImportarAulas(true)}
                      disabled={subjects.length === 0 || importandoAulas}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        subjects.length === 0 || importandoAulas
                          ? 'bg-slate-500 text-white cursor-not-allowed'
                          : 'text-white bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
                      }`}
                      title="Baixar a planilha em Excel, preencher ou corrigir várias aulas e enviar de volta"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {importandoAulas ? 'Importando...' : 'Registrar por planilha'}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => (diaFiltrado ? abrirFormularioNoDia(diaFiltrado) : handleNewEntry())}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25"
                    >
                      {diaFiltrado ? '+ Nova aula neste dia' : '+ Nova Aula'}
                    </button>
                  )}
                </div>
              )}
              {showEntryForm && (
                <button
                  onClick={() => { setShowEntryForm(false); resetEntryForm(); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode
                    ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  Cancelar
                </button>
              )}
            </div>

            {resultadoAulas && !showEntryForm && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                resultadoAulas.success
                  ? darkMode
                    ? 'bg-[#13983D]/10 border border-[#13983D]/20 text-[#13983D]'
                    : 'bg-[#13983D]/5 border border-[#13983D]/20 text-[#13983D]'
                  : darkMode
                    ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]'
                    : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20 text-[#E83A3C]'
              }`}>
                {resultadoAulas.success ? (
                  <div>
                    <p className="font-medium">
                      {resultadoAulas.created || resultadoAulas.updated
                        ? [
                          resultadoAulas.created === 1
                            ? '1 aula nova registrada'
                            : resultadoAulas.created > 1 && `${resultadoAulas.created} aulas novas registradas`,
                          resultadoAulas.updated === 1
                            ? '1 aula atualizada'
                            : resultadoAulas.updated > 1 && `${resultadoAulas.updated} aulas atualizadas`,
                        ].filter(Boolean).join(' · ') + '.'
                        : 'Planilha lida: nenhuma aula nova ou alterada.'}
                    </p>
                    {resultadoAulas.errors && resultadoAulas.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-[#F9B047]">
                          Não entraram ({resultadoAulas.errors.length}) — corrija na planilha e envie só essas linhas de novo:
                        </p>
                        {resultadoAulas.errors.slice(0, 8).map((err, i) => (
                          <p key={i} className="text-xs text-[#F9B047]">{err}</p>
                        ))}
                        {resultadoAulas.errors.length > 8 && (
                          <p className="text-xs text-[#F9B047]">...e mais {resultadoAulas.errors.length - 8} avisos</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p>{resultadoAulas.error || 'Erro ao importar planilha.'}</p>
                )}
              </div>
            )}

            {mostrarComoImportarAulas && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <div className={`w-full max-w-lg max-h-full overflow-y-auto rounded-2xl p-6 ${darkMode
                  ? 'bg-slate-800 border border-slate-700'
                  : 'bg-white shadow-xl'}`}>
                  <h2 className={`text-xl font-semibold mb-1 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    Registrar aulas por planilha
                  </h2>
                  <p className={`text-sm mb-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Serve para lançar várias aulas de uma vez, preenchendo no
                    computador. São três passos.
                  </p>

                  <ol className="space-y-4">
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">1</span>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Baixe a planilha desta turma
                        </p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Vem em Excel, uma aba por disciplina, com as aulas já
                          registradas preenchidas e linhas em branco para as novas.
                          Pode repassar o arquivo ao professor por e-mail ou WhatsApp.
                        </p>
                        <button
                          onClick={handleBaixarModeloAulas}
                          disabled={baixandoModeloAulas}
                          className={`mt-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white ${
                            baixandoModeloAulas ? 'bg-slate-500' : 'bg-[#0974BA] hover:bg-[#0974BA]/80'
                          }`}
                        >
                          {baixandoModeloAulas ? 'Baixando...' : 'Baixar planilha em Excel'}
                        </button>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">2</span>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Preencha uma linha por aula, na aba da disciplina
                        </p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <b>Data</b>, <b>Conteúdo ministrado e metodologia</b> e
                          <b> Habilidades BNCC</b> são obrigatórios; a <b>Disciplina</b>
                          pode ficar em branco (vale a da aba) e <b>Observações</b> é
                          opcional. Nº da aula e Tipo só são necessários em aula dupla
                          no mesmo dia ou em reposição.
                        </p>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          Para corrigir uma aula já registrada, edite a linha
                          dela. Aula nova vai numa linha em branco — não copie
                          uma linha registrada. Apagar a linha não exclui a aula.
                        </p>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#13983D] text-white text-xs font-bold flex items-center justify-center">3</span>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Envie o arquivo preenchido
                        </p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Linhas novas viram aulas na lista e as corrigidas
                          atualizam a aula; as que não mudaram ficam como estão.
                          A linha com problema (data futura, feriado, aula
                          repetida, sem BNCC) aparece como aviso — as outras entram.
                        </p>
                        <label className={`inline-flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer text-white ${
                          importandoAulas ? 'bg-slate-500 cursor-not-allowed' : 'bg-[#13983D] hover:bg-[#13983D]/80'
                        }`}>
                          {importandoAulas ? 'Importando...' : 'Escolher arquivo preenchido'}
                          <input
                            type="file"
                            accept=".xlsx"
                            onChange={(e) => { setMostrarComoImportarAulas(false); handleImportarAulas(e); }}
                            disabled={importandoAulas}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </li>
                  </ol>

                  <div className="flex justify-end mt-6 pt-4 border-t border-slate-700/10">
                    <button
                      onClick={() => setMostrarComoImportarAulas(false)}
                      className={`px-5 py-2.5 rounded-xl text-sm font-medium ${darkMode
                        ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Formulário de Registro */}
            {showEntryForm && (
              <div className={`rounded-xl p-5 mb-4 ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                {editingEntry && conteudoAutomatico(editingEntry.content_description) && (
                  <div className={`rounded-lg p-3 mb-4 ${darkMode
                    ? 'bg-[#F9B047]/10 border border-[#F9B047]/30'
                    : 'bg-[#F9B047]/10 border border-[#F9B047]/40'}`}>
                    <p className={`text-xs font-semibold ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                      Esta aula foi criada ao lançar a frequência.
                    </p>
                    <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      A chamada já está salva. Falta registrar o conteúdo ministrado e as
                      habilidades BNCC — só então a aula conta como concluída.
                    </p>
                  </div>
                )}
                {/* Três colunas, e os horários descem para a linha de baixo.
                    Com quatro campos lado a lado o formulário tem ~612px (o
                    calendário fica à esquerda), e os dois `input[type=time]`
                    dividiam uma única coluna: sobravam ~42px úteis e o horário
                    que a aula escolhida acabou de preencher aparecia como "0" —
                    o professor não tinha como conferir na tela o que gravou.
                    Espremer em cinco colunas só passava o aperto para a data. */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Data</label>
                    <input
                      type="date"
                      value={entryForm.lesson_date}
                      max={hojeLocal()}
                      onChange={(e) => setEntryForm(prev => ({ ...prev, lesson_date: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                        ? 'bg-slate-600 border-slate-500 text-slate-200'
                        : 'bg-white border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                    />
                    {getFeriadoNacional(entryForm.lesson_date) && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                        Feriado nacional ({getFeriadoNacional(entryForm.lesson_date)}) — não há aula nesta data.
                      </p>
                    )}
                  </div>
                  {/* O tipo vem antes do horário: é ele que decide quais
                      horários fazem sentido oferecer. */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Tipo de Aula</label>
                    <SearchableSelect
                      value={entryForm.lesson_type}
                      onChange={(v) => setEntryForm(prev => ({
                        ...prev,
                        lesson_type: v,
                        // Deixou de ser substituição: o horário do colega sai
                        // junto. Mantê-lo gravaria a aula no período de outra
                        // pessoa sem que a tela estivesse mais dizendo isso.
                        ...(v !== 'extra' && aulaSubstituida
                          ? { class_period: '', start_time: '', end_time: '' }
                          : {}),
                      }))}
                      options={lessonTypeOptions}
                      placeholder="Selecione..."
                      darkMode={darkMode}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Aula do dia
                    </label>
                    <SearchableSelect
                      value={entryForm.class_period}
                      onChange={(periodo) => {
                        const slot = slotsDaDisciplina.find(s => String(s.period) === String(periodo));
                        setEntryForm(prev => ({
                          ...prev,
                          class_period: periodo,
                          start_time: slot?.start_time ? slot.start_time.slice(0, 5) : prev.start_time,
                          end_time: slot?.end_time ? slot.end_time.slice(0, 5) : prev.end_time,
                        }));
                      }}
                      options={slotsDaDisciplina.map(slot => ({
                        value: slot.period,
                        label: rotuloDaAula(slot),
                      }))}
                      emptyOptionLabel={
                        loadingDaySlots
                          ? 'Carregando aulas...'
                          : slotsDaDisciplina.length === 0
                          ? 'Sem aula na grade (aula extra)'
                          : 'Selecione a aula...'
                      }
                      darkMode={darkMode}
                      className="w-full"
                    />
                    {aulaSubstituida ? (
                      // Curto de propósito: esta coluna tem ~155px, e o que a
                      // substituição significa (de quem é o horário, que ele
                      // conta como cumprido) já é dito logo abaixo, na faixa de
                      // permuta, que ocupa a linha inteira. Aqui só marca que a
                      // aula escolhida não é a dele.
                      <p className="text-[11px] mt-1 text-[#F9B047]">
                        Você está cobrindo este horário.
                      </p>
                    ) : entryForm.lesson_type === 'extra' ? (
                      // Foi este tipo que fez os horários dos colegas
                      // aparecerem; dizer para que servem evita que ele
                      // escolha um sem saber o que está afirmando.
                      <p className={`text-[11px] mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Cobriu um colega? Escolha o horário dele.
                      </p>
                    ) : (
                      <p className={`text-[11px] mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        A frequência vale só para esta aula.
                      </p>
                    )}
                  </div>
                  {/* Segunda linha: os horários vêm da grade, então o lugar
                      deles é depois da aula que os define. */}
                  <div className="grid grid-cols-2 gap-2 md:col-span-2">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Início</label>
                      <input
                        type="time"
                        min="06:00"
                        max="22:00"
                        value={entryForm.start_time}
                        onChange={(e) => setEntryForm(prev => ({ ...prev, start_time: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-600 border-slate-500 text-slate-200'
                          : 'bg-white border-slate-200 text-slate-700'
                        } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Fim</label>
                      <input
                        type="time"
                        min="06:00"
                        max="22:00"
                        value={entryForm.end_time}
                        onChange={(e) => setEntryForm(prev => ({ ...prev, end_time: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-600 border-slate-500 text-slate-200'
                          : 'bg-white border-slate-200 text-slate-700'
                        } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                      />
                    </div>
                  </div>
                </div>

                {/* Aulas geminadas: o mesmo registro vale para as outras aulas
                    dele no dia, sem digitar o conteúdo de novo. */}
                {aulasDoBloco.length > 0 && (
                  <div className={`rounded-lg p-3 mb-4 ${darkMode
                    ? 'bg-[#0974BA]/10 border border-[#0974BA]/25'
                    : 'bg-[#0974BA]/5 border border-[#0974BA]/20'}`}>
                    <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      Aula geminada? Registrar também:
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {aulasDoBloco.map(slot => {
                        const marcada = geminadasMarcadas.some(p => String(p) === String(slot.period));
                        return (
                          <label
                            key={slot.period}
                            className={`flex items-center gap-2 text-xs cursor-pointer ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}
                          >
                            <input
                              type="checkbox"
                              checked={marcada}
                              onChange={() => setEntryForm(prev => ({
                                ...prev,
                                aulas_geminadas: marcada
                                  ? prev.aulas_geminadas.filter(p => String(p) !== String(slot.period))
                                  : [...prev.aulas_geminadas, slot.period],
                              }))}
                              className="w-4 h-4 accent-[#0974BA]"
                            />
                            {rotuloDaAula(slot)}
                          </label>
                        );
                      })}
                    </div>
                    <p className={`text-[11px] mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Cada aula marcada é gravada com este mesmo conteúdo, e a chamada
                      lançada a seguir vale para todas elas.
                    </p>
                  </div>
                )}

                {entryForm.lesson_type === 'nao_realizada' && (
                  <div className={`rounded-lg p-4 mb-4 ${darkMode
                    ? 'bg-slate-600/40 border border-slate-500'
                    : 'bg-slate-100 border border-slate-300'}`}>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Motivo de a aula não ter acontecido *
                    </label>
                    <input
                      type="text"
                      value={entryForm.not_held_reason}
                      onChange={(e) => setEntryForm(prev => ({ ...prev, not_held_reason: e.target.value }))}
                      placeholder="Ex.: professora em formação continuada, sala interditada"
                      className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                        ? 'bg-slate-700 border-slate-500 text-slate-200 placeholder-slate-400'
                        : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                    />
                    <p className={`text-[11px] mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      O horário sai da cobrança da coordenação com este motivo registrado,
                      em vez de ficar pendente para sempre. Não há chamada a lançar.
                    </p>
                  </div>
                )}

                {/* Permuta: a grade prevê outra disciplina neste horário. */}
                {entryForm.class_period
                  && entryForm.lesson_type !== 'nao_realizada'
                  && (() => {
                    const previsto = daySlots.find(
                      s => String(s.period) === String(entryForm.class_period)
                    );
                    if (!previsto || String(previsto.id_subject) === String(selectedSubject)) {
                      return null;
                    }
                    return (
                      <div className={`rounded-lg p-3 mb-4 ${darkMode
                        ? 'bg-[#7C3AED]/15 border border-[#7C3AED]/30'
                        : 'bg-[#7C3AED]/5 border border-[#7C3AED]/25'}`}>
                        <p className="text-xs font-semibold text-[#7C3AED]">
                          Permuta: a grade prevê {previsto.subject_name} nesta aula
                          {previsto.teacher_name ? `, com ${previsto.teacher_name}` : ''}.
                        </p>
                        <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Pode registrar normalmente — o horário aparece como cumprido,
                          com a disciplina prevista ao lado da que aconteceu.
                        </p>
                      </div>
                    );
                  })()}

                {aulaJaRegistrada && (() => {
                  const registroDaLista = diaryEntries.find(
                    e => e.id === aulaJaRegistrada.id_diary_entry
                  );
                  const disciplina = aulaJaRegistrada.actual_subject_name
                    || aulaJaRegistrada.subject_name;
                  const faltaChamada = !aulaJaRegistrada.attendance_filled
                    && !aulaJaRegistrada.not_held;
                  return (
                    <div className={`rounded-lg p-3 mb-4 ${darkMode
                      ? 'bg-[#F9B047]/10 border border-[#F9B047]/30'
                      : 'bg-[#F9B047]/10 border border-[#F9B047]/40'}`}>
                      <p className={`text-xs font-semibold ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                        Esta aula já está registrada{disciplina ? ` (${disciplina})` : ''}.
                      </p>
                      <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {aulaJaRegistrada.not_held
                          ? 'Ela foi registrada como não realizada. Não é preciso gravar de novo.'
                          : faltaChamada
                          ? 'Falta só lançar a frequência dela — não é preciso gravar a aula de novo.'
                          : 'A frequência dela também já foi lançada. Não é preciso gravar de novo.'}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {faltaChamada && aulaJaRegistrada.can_edit !== false && (
                          <button
                            type="button"
                            onClick={() => abrirChamadaDaAula({
                              data: entryForm.lesson_date,
                              periodo: aulaJaRegistrada.period,
                              idAula: aulaJaRegistrada.id_diary_entry,
                            })}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-[#13983D] to-[#0974BA]"
                          >
                            Lançar frequência
                          </button>
                        )}
                        {/* Só a aula desta disciplina: o formulário grava com a
                            disciplina do topo, e abrir aqui a de outra trocaria
                            a disciplina dela ao salvar. */}
                        {registroDaLista && (
                          <button
                            type="button"
                            onClick={() => handleEditEntry(registroDaLista)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode
                              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                              : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'}`}
                          >
                            Abrir o registro
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {!aulaJaRegistrada && registradasNaData.length > 0 && (
                  <div className={`rounded-lg p-3 mb-4 ${repeteAulaSemGrade
                    ? darkMode ? 'bg-[#F9B047]/10 border border-[#F9B047]/30' : 'bg-[#F9B047]/10 border border-[#F9B047]/40'
                    : darkMode ? 'bg-slate-700/50 border border-slate-600' : 'bg-white border border-slate-200'}`}>
                    <p className={`text-xs font-semibold ${repeteAulaSemGrade
                      ? darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'
                      : darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      {repeteAulaSemGrade
                        ? 'A aula deste dia já está registrada.'
                        : `Já registrado neste dia (${registradasNaData.length}):`}
                    </p>
                    {repeteAulaSemGrade && (
                      <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Para corrigir, abra o registro abaixo. Se houve mesmo uma segunda
                        aula no dia, mude o Tipo de Aula para Reposição ou Extra. O horário
                        de início e fim não separa uma aula da outra: para as duas contarem
                        como aulas da grade, a coordenação monta a grade horária da turma
                        (Turmas › Grade Horária).
                      </p>
                    )}
                    <ul className="mt-2 space-y-2">
                      {registradasNaData.map(e => (
                        <li key={e.id} className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold">
                              {e.period_label || lessonTypeOptions.find(o => o.value === e.lesson_type)?.label || 'Aula'}
                            </span>
                            {' — '}
                            <span className="break-words">{(e.content_description || '').slice(0, 90)}{(e.content_description || '').length > 90 ? '…' : ''}</span>
                            <span className={`ml-1 ${e.attendance_filled ? 'text-[#13983D]' : darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                              {e.attendance_filled ? '· com frequência' : e.lesson_type === 'nao_realizada' ? '' : '· falta frequência'}
                              {aulaSemConteudo(e) ? ' · falta conteúdo' : ''}
                            </span>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {!e.attendance_filled && e.lesson_type !== 'nao_realizada' && (
                              <button
                                type="button"
                                onClick={() => abrirChamadaDaAula({ data: e.lesson_date, periodo: e.class_period, idAula: e.id })}
                                className="px-2 py-1 rounded-md font-medium text-white bg-gradient-to-r from-[#13983D] to-[#0974BA]"
                              >
                                Lançar frequência
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleEditEntry(e)}
                              className={`px-2 py-1 rounded-md font-medium ${darkMode
                                ? 'bg-slate-600 text-slate-100 hover:bg-slate-500'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                            >
                              Abrir
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {ehPolivalente && entryForm.lesson_type !== 'nao_realizada' && (
                <div className="mb-4">
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Componente <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>(opcional)</span>
                  </label>
                  <SearchableSelect
                    value={entryForm.id_componente}
                    onChange={(v) => setEntryForm(prev => ({ ...prev, id_componente: v ? String(v) : '' }))}
                    options={componentesPoli.componentes.map(c => ({ value: String(c.id), label: c.subject_name }))}
                    placeholder="Língua Portuguesa, Matemática..."
                    darkMode={darkMode}
                    className="w-full sm:w-80"
                  />
                  <p className={`mt-1 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    A aula fica no Polivalente; o componente diz o que foi trabalhado.
                  </p>
                </div>
                )}

                {entryForm.lesson_type !== 'nao_realizada' && (
                <div className="mb-4">
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Conteúdo Ministrado e Metodologia *
                  </label>
                  <textarea
                    value={entryForm.content_description}
                    onChange={(e) => setEntryForm(prev => ({ ...prev, content_description: e.target.value }))}
                    rows={4}
                    placeholder="Descreva o conteúdo trabalhado em sala e a metodologia usada..."
                    className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-600 border-slate-500 text-slate-200 placeholder-slate-400'
                      : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  />
                </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {entryForm.lesson_type !== 'nao_realizada' && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Habilidades BNCC *
                    </label>
                    <input
                      type="text"
                      value={entryForm.bncc_skills}
                      onChange={(e) => setEntryForm(prev => ({ ...prev, bncc_skills: e.target.value }))}
                      placeholder="Ex: EF05MA01, EF05MA02"
                      required
                      className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                        ? 'bg-slate-600 border-slate-500 text-slate-200 placeholder-slate-400'
                        : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                    />
                  </div>
                  )}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Observações
                    </label>
                    <input
                      type="text"
                      value={entryForm.observations}
                      onChange={(e) => setEntryForm(prev => ({ ...prev, observations: e.target.value }))}
                      placeholder="Observações adicionais..."
                      className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                        ? 'bg-slate-600 border-slate-500 text-slate-200 placeholder-slate-400'
                        : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                    />
                  </div>
                </div>

                {entryError && (
                  <div className={`p-3 rounded-lg mb-4 ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                    <p className="text-sm text-[#E83A3C]">{entryError}</p>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => { setShowEntryForm(false); resetEntryForm(); }}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium ${darkMode
                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                    disabled={savingEntry}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveEntry}
                    disabled={savingEntry || !!getFeriadoNacional(entryForm.lesson_date) || !!aulaJaRegistrada || repeteAulaSemGrade}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${
                      savingEntry || getFeriadoNacional(entryForm.lesson_date) || aulaJaRegistrada || repeteAulaSemGrade
                        ? 'bg-slate-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
                    }`}
                  >
                    {savingEntry ? 'Salvando...' : editingEntry ? 'Atualizar Registro' : 'Gravar Registro'}
                  </button>
                </div>
              </div>
            )}

            {/* Lista de Registros */}
            {!showEntryForm && diaFiltrado && (
              <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 mb-3 ${darkMode
                ? 'bg-[#0974BA]/15 border border-[#0974BA]/30'
                : 'bg-[#0974BA]/5 border border-[#0974BA]/20'}`}>
                <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  Aulas registradas em {new Date(diaFiltrado + 'T00:00:00').toLocaleDateString('pt-BR')}
                </span>
                <button
                  type="button"
                  onClick={() => setDiaFiltrado(null)}
                  className="text-xs font-semibold text-[#0974BA] hover:underline"
                >
                  Ver o mês inteiro
                </button>
              </div>
            )}
            {!showEntryForm && (
              <>
                {loadingEntries ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : diaryEntries.length === 0 ? (
                  <div className={`rounded-xl p-8 text-center ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                    <div className="text-4xl mb-3">📝</div>
                    <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                      Nenhum registro de aula para este mês.
                    </p>
                    {canEdit && (
                      <button
                        onClick={handleNewEntry}
                        className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974]"
                      >
                        Registrar primeira aula
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {diaryEntries
                      .filter(entry => !diaFiltrado || entry.lesson_date === diaFiltrado)
                      .map(entry => {
                      // Mesma regra do verde do calendário.
                      const concluida = aulaResolvida(entry) && entry.lesson_type !== 'nao_realizada';
                      const faltaChamada = !entry.attendance_filled && entry.lesson_type !== 'nao_realizada';
                      const faltaConteudo = aulaSemConteudo(entry);
                      return (
                      <div
                        key={entry.id}
                        className={`rounded-xl p-4 transition-all ${
                          concluida
                            ? darkMode
                              ? 'bg-[#13983D]/10 border border-[#13983D]/20'
                              : 'bg-[#13983D]/5 border border-[#13983D]/20'
                            : darkMode
                            ? 'bg-slate-700/30 border border-slate-600/30 hover:border-slate-500/50'
                            : 'bg-slate-50 border border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3 md:gap-4">
                          {/* `min-w-0` é o que permite este bloco encolher: sem
                              ele um item flex não passa da largura do próprio
                              conteúdo, e o conteúdo da aula empurrava a página
                              inteira para fora da tela no celular. */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                {new Date(entry.lesson_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                              </span>
                              {entry.class_period && (
                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#2E4974]/15 text-[#2E4974] dark:bg-[#0974BA]/25 dark:text-[#7cc0ec]">
                                  {entry.class_period}ª aula
                                </span>
                              )}
                              {entry.start_time && (
                                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {entry.start_time.slice(0, 5)}{entry.end_time ? ` - ${entry.end_time.slice(0, 5)}` : ''}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                entry.lesson_type === 'extra'
                                  ? 'bg-[#F9B047]/20 text-[#F9B047]'
                                  : entry.lesson_type === 'reposicao'
                                  ? 'bg-[#0974BA]/20 text-[#0974BA]'
                                  : entry.lesson_type === 'laboratorio'
                                  ? 'bg-[#13983D]/20 text-[#13983D]'
                                  : darkMode
                                  ? 'bg-slate-600/50 text-slate-300'
                                  : 'bg-slate-200 text-slate-600'
                              }`}>
                                {lessonTypeOptions.find(o => o.value === entry.lesson_type)?.label || entry.lesson_type}
                              </span>
                              {concluida ? (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#13983D]/20 text-[#13983D]">
                                  Concluída
                                </span>
                              ) : (
                                <>
                                  {faltaConteudo && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium bg-[#F9B047]/20 ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                                      Falta conteúdo
                                    </span>
                                  )}
                                  {faltaChamada && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium bg-[#F9B047]/20 ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>
                                      Falta frequência
                                    </span>
                                  )}
                                </>
                              )}
                              {entry.not_held_reason && (
                                <span
                                  className="px-2 py-0.5 rounded text-xs font-medium bg-slate-500/20 text-slate-500"
                                  title={entry.not_held_reason}
                                >
                                  Não houve aula
                                </span>
                              )}
                            </div>
                            <p className={`text-sm mb-1 break-words ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                              {entry.componente_name && (
                                <span className={`mr-1.5 px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-[#0974BA]/25 text-[#7CC4F0]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                                  {entry.componente_name}
                                </span>
                              )}
                              {entry.content_description}
                            </p>
                            {entry.bncc_skills && (
                              <p className={`text-xs break-words ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                BNCC: {entry.bncc_skills}
                              </p>
                            )}
                            {entry.attendance_count && entry.attendance_count.total > 0 && (
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs text-[#13983D]">{entry.attendance_count.presentes}P</span>
                                <span className="text-xs text-[#E83A3C]">{entry.attendance_count.ausentes}F</span>
                                <span className="text-xs text-[#F9B047]">{entry.attendance_count.justificados}J</span>
                              </div>
                            )}
                            {canEdit && faltaConteudo && (
                              <button
                                type="button"
                                onClick={() => handleEditEntry(entry)}
                                className={`mt-2 mr-4 text-xs font-semibold hover:underline ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}
                              >
                                Completar registro (conteúdo e BNCC) →
                              </button>
                            )}
                            {canEdit && faltaChamada && (
                              <button
                                type="button"
                                onClick={() => abrirChamadaDaAula({
                                  data: entry.lesson_date,
                                  periodo: entry.class_period,
                                  idAula: entry.id,
                                })}
                                className={`mt-2 text-xs font-semibold hover:underline ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}
                              >
                                Lançar frequência desta aula →
                              </button>
                            )}
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {/* Com a chamada lançada a aula já conta como
                                  concluída; o ✓ manual fica para a que não tem. */}
                              {!entry.attendance_filled && (
                              <button
                                onClick={() => handleToggleComplete(entry.id)}
                                className={`p-2 rounded-lg text-xs transition-colors ${
                                  entry.is_completed
                                    ? 'bg-[#F9B047]/20 text-[#F9B047] hover:bg-[#F9B047]/30'
                                    : 'bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30'
                                }`}
                                title={entry.is_completed ? 'Reabrir aula' : 'Marcar como concluída'}
                              >
                                {entry.is_completed ? '↩' : '✓'}
                              </button>
                              )}
                              <button
                                onClick={() => handleEditEntry(entry)}
                                className={`p-2 rounded-lg text-xs transition-colors ${darkMode
                                  ? 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                                }`}
                                title="Editar"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteEntry(entry.id)}
                                className="p-2 rounded-lg text-xs bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30 transition-colors"
                                title="Excluir"
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ============ ABA: FREQUÊNCIA ============
          Entrada pela DATA: escolhe-se a aula do dia e lança-se a chamada
          dela. A aula pode ainda não estar registrada no diário — o backend
          a cria a partir do slot da grade. Substituiu as antigas "Frequência
          por Aula" e "Chamada do Dia", que gravavam a mesma chamada. O
          resumo do dia sai da consolidação (api/attendance_sync.py). ============ */}
      {activeTab === 'frequencia' && (
        <>
          <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Escolha a data, escolha a aula e lance a chamada. Não é preciso
            registrar a aula antes em <strong>Registro de Aulas</strong>: se ela
            ainda não estiver lá, entra no diário ao salvar a chamada.
          </p>
          {/* A disciplina do topo vale aqui também: sem ela o calendário
              marcava dias de outras disciplinas como se fossem desta. */}
          <Frequencia embedded idSubject={selectedSubject} aulaInicial={aulaParaChamada} />
        </>
      )}

      {/* ============ ABA: PLANILHA ============ */}
      {activeTab === 'planilha' && (
        <div className={`rounded-xl p-5 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <h3 className={`text-sm font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Frequência do mês
              </h3>
              <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Uma linha por aluno, uma coluna por aula. Clique numa célula
                para alternar entre P, F, J e A.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Mês:</label>
              <input
                type="month"
                value={spreadsheetMonth}
                onChange={(e) => setSpreadsheetMonth(e.target.value)}
                className={`px-3 py-2 rounded-lg text-sm ${darkMode
                  ? 'bg-slate-700 border-slate-600 text-slate-200'
                  : 'bg-white border-slate-200 text-slate-700'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
              />
              {Object.keys(spreadsheetChanges).length > 0 && canEdit && (
                <button
                  onClick={handleSaveSpreadsheet}
                  disabled={savingSpreadsheet}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all ${
                    savingSpreadsheet
                      ? 'bg-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg'
                  }`}
                >
                  {savingSpreadsheet ? 'Salvando...' : `Salvar (${Object.keys(spreadsheetChanges).length} alterações)`}
                </button>
              )}
            </div>
          </div>

          {/* Ações. Ficam depois da planilha de propósito: o que a tela é
              vem primeiro, e exportar/importar é o que se faz com ela. */}
          <div className={`rounded-xl p-4 mb-4 ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
            <div className="flex flex-wrap items-center gap-3">
              {/* Baixar Template */}
              <button
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  downloadingTemplate
                    ? 'bg-slate-500 text-white cursor-not-allowed'
                    : 'text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                }`}
                title="Excel com um dia do mês por coluna, já com o que está lançado"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {downloadingTemplate ? 'Baixando...' : 'Baixar em Excel'}
              </button>

              {/* Baixar PDF da planilha */}
              <button
                onClick={handleDownloadPlanilhaPdf}
                disabled={downloadingPdf === 'planilha'}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  downloadingPdf === 'planilha'
                    ? 'bg-slate-500 text-white cursor-not-allowed'
                    : 'text-white bg-gradient-to-r from-[#E83A3C] to-[#C4292B] hover:shadow-lg hover:shadow-[#E83A3C]/25'
                }`}
                title="Mesma tabela da tela, para imprimir ou arquivar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {downloadingPdf === 'planilha' ? 'Gerando...' : 'Baixar em PDF'}
              </button>

              {/* Importar Planilha — abre o passo a passo, não o seletor de
                  arquivo: quem nunca baixou a planilha não sabe o que enviar. */}
              {canEdit && (
                <button
                  onClick={() => setMostrarComoImportar(true)}
                  disabled={importingFile}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    importingFile
                      ? 'bg-slate-500 text-white cursor-not-allowed'
                      : 'text-white bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {importingFile ? 'Importando...' : 'Importar planilha preenchida'}
                </button>
              )}
            </div>

            {/* Resultado da importação */}
            {importResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${
                importResult.success
                  ? darkMode
                    ? 'bg-[#13983D]/10 border border-[#13983D]/20 text-[#13983D]'
                    : 'bg-[#13983D]/5 border border-[#13983D]/20 text-[#13983D]'
                  : darkMode
                    ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]'
                    : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20 text-[#E83A3C]'
              }`}>
                {importResult.success ? (
                  <div>
                    <p className="font-medium">Importação realizada com sucesso!</p>
                    <p className="text-xs mt-1">
                      {importResult.rows_processed} alunos processados -
                      {importResult.created} registros criados,
                      {importResult.updated} atualizados
                    </p>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-[#F9B047]">Avisos:</p>
                        {importResult.errors.slice(0, 5).map((err, i) => (
                          <p key={i} className="text-xs text-[#F9B047]">{err}</p>
                        ))}
                        {importResult.errors.length > 5 && (
                          <p className="text-xs text-[#F9B047]">...e mais {importResult.errors.length - 5} avisos</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p>{importResult.error || 'Erro ao importar planilha.'}</p>
                )}
              </div>
            )}
          </div>

          {loadingSpreadsheet ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !spreadsheetData || spreadsheetData.entries?.length === 0 ? (
            <div className={`rounded-xl p-8 text-center ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
              <div className="text-4xl mb-3">📊</div>
              <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Nenhuma aula registrada em {mesPorExtenso(spreadsheetMonth)}.
              </p>
              {/* O motivo importa: sem aula não há coluna, e a planilha fica
                  com os alunos e nenhum dia — foi o que pareceu defeito. */}
              <p className={`text-sm mt-2 max-w-lg mx-auto ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                A planilha tem uma coluna por aula registrada. Registre as
                aulas do mês para ela ter o que mostrar — ou baixe o Excel,
                que traz todos os dias do mês para preencher de uma vez.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
                <button
                  onClick={() => setActiveTab('registro')}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974]"
                >
                  Ir para Registro de Aulas
                </button>
                <button
                  onClick={() => navigate(`/turmas/${id}/grade-horaria`)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium ${darkMode
                    ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'}`}
                >
                  Conferir a grade horária
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700' : 'bg-slate-100'}>
                    <th className={`text-left px-3 py-2 sticky left-0 z-10 ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                      Aluno
                    </th>
                    {/* Mesma turma pode ter mais de uma aula no dia: só o
                        número do dia deixava colunas idênticas. */}
                    {spreadsheetData.entries.map(entry => {
                      const aulasNoDia = spreadsheetData.entries.filter(
                        e => e.date === entry.date
                      ).length;
                      return (
                        <th
                          key={entry.id}
                          className={`text-center px-2 py-2 min-w-[40px] ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}
                          title={`${entry.date}${entry.class_period ? ` · ${entry.class_period}ª aula` : ''}`
                            + `${entry.subject_name ? ` · ${entry.subject_name}` : ''}`}
                        >
                          <div>{new Date(entry.date + 'T00:00:00').getDate()}</div>
                          {aulasNoDia > 1 && entry.class_period && (
                            <div className={`text-[9px] font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              {entry.class_period}ª
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-slate-600' : 'divide-slate-200'}`}>
                  {spreadsheetData.students.map(student => (
                    <tr key={student.id_student} className={`${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`}>
                      <td className={`px-3 py-2 font-medium whitespace-nowrap sticky left-0 ${darkMode ? 'bg-slate-800/50 text-slate-200' : 'bg-white/80 text-slate-700'}`}>
                        {student.student_name}
                      </td>
                      {spreadsheetData.entries.map(entry => {
                        const att = student.attendances[String(entry.id)];
                        const changeKey = `${student.id_student}-${entry.id}`;
                        const currentStatus = spreadsheetChanges[changeKey]?.status || att?.status || null;
                        const statusCycle = ['presente', 'ausente', 'justificado', 'atrasado'];
                        const statusLabels = { presente: 'P', ausente: 'F', justificado: 'J', atrasado: 'A' };
                        const statusColors = {
                          presente: 'bg-[#13983D] text-white',
                          ausente: 'bg-[#E83A3C] text-white',
                          justificado: 'bg-[#F9B047] text-white',
                          atrasado: 'bg-[#0974BA] text-white',
                        };

                        return (
                          <td key={entry.id} className="px-1 py-1 text-center">
                            <button
                              disabled={!canEdit}
                              onClick={() => {
                                const currentIdx = statusCycle.indexOf(currentStatus);
                                const nextStatus = statusCycle[(currentIdx + 1) % statusCycle.length];
                                handleSpreadsheetChange(student.id_student, entry.id, nextStatus);
                              }}
                              className={`w-8 h-8 rounded text-xs font-bold transition-all ${
                                currentStatus
                                  ? statusColors[currentStatus]
                                  : darkMode
                                  ? 'bg-slate-600/50 text-slate-400 hover:bg-slate-600'
                                  : 'bg-slate-200 text-slate-400 hover:bg-slate-300'
                              } ${!canEdit ? 'cursor-not-allowed' : 'hover:scale-110'}`}
                              title={currentStatus || 'Sem registro'}
                            >
                              {currentStatus ? statusLabels[currentStatus] : '-'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Passo a passo da importação. O botão antes abria o seletor de
              arquivo direto, e quem nunca tinha baixado a planilha não sabia
              qual arquivo mandar — nem que precisava baixar um primeiro. */}
          {mostrarComoImportar && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
                ? 'bg-slate-800 border border-slate-700'
                : 'bg-white shadow-xl'}`}>
                <h2 className={`text-xl font-semibold mb-1 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  Importar planilha preenchida
                </h2>
                <p className={`text-sm mb-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Serve para lançar o mês inteiro de uma vez, sem clicar célula
                  por célula. São três passos.
                </p>

                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">1</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Baixe a planilha deste mês
                      </p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Ela vem com os alunos da turma nas linhas, os dias de{' '}
                        {mesPorExtenso(spreadsheetMonth)} nas colunas e o que já
                        está lançado preenchido. É este arquivo que você vai
                        devolver — não vale montar uma planilha do zero.
                      </p>
                      <button
                        onClick={handleDownloadTemplate}
                        disabled={downloadingTemplate}
                        className={`mt-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white ${
                          downloadingTemplate ? 'bg-slate-500' : 'bg-[#0974BA] hover:bg-[#0974BA]/80'
                        }`}
                      >
                        {downloadingTemplate ? 'Baixando...' : 'Baixar em Excel'}
                      </button>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">2</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Preencha as células com uma letra
                      </p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <b>P</b> presente · <b>F</b> falta · <b>J</b> falta
                        justificada · <b>A</b> atrasado. Célula em branco fica
                        como está — não apaga o que já foi lançado.
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Não mexa nos nomes, na matrícula nem no cabeçalho: é por
                        eles que o sistema reconhece cada aluno e cada dia.
                      </p>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#13983D] text-white text-xs font-bold flex items-center justify-center">3</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Envie o arquivo salvo
                      </p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        O mesmo arquivo que você baixou, agora preenchido
                        (.xlsx). A planilha da tela atualiza na hora.
                      </p>
                      <label className={`inline-flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer text-white ${
                        importingFile ? 'bg-slate-500 cursor-not-allowed' : 'bg-[#13983D] hover:bg-[#13983D]/80'
                      }`}>
                        {importingFile ? 'Importando...' : 'Escolher arquivo preenchido'}
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => { setMostrarComoImportar(false); handleImportFile(e); }}
                          disabled={importingFile}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </li>
                </ol>

                <div className="flex justify-end mt-6 pt-4 border-t border-slate-700/10">
                  <button
                    onClick={() => setMostrarComoImportar(false)}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium ${darkMode
                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Legenda da planilha */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-600/30">
            {attendanceStatusOptions.map(option => (
              <div key={option.value} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white ${
                  option.color === 'green' ? 'bg-[#13983D]' :
                  option.color === 'red' ? 'bg-[#E83A3C]' :
                  option.color === 'yellow' ? 'bg-[#F9B047]' :
                  'bg-[#0974BA]'
                }`}>
                  {option.short}
                </div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{option.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ ABA: NOTAS ============ */}
      {activeTab === 'notas' && serieHabilidades && (
        <RegistroHabilidades
          turmaId={id}
          disciplinaId={matrizInfantil ? selectedSubject : disciplinaDaNota}
          componentes={ehPolivalente && !matrizInfantil ? componentesPoli.componentes : null}
          componenteSelecionado={componenteNota}
          onSelecionarComponente={setComponenteNota}
          anoLetivo={notasYear}
          onAnoLetivo={setNotasYear}
          canEdit={canEdit}
          darkMode={darkMode}
          downloadPdf={downloadPdf}
          baixandoPdf={downloadingPdf}
        />
      )}

      {activeTab === 'notas' && !serieHabilidades && (
        <div className={`rounded-xl p-5 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className={`text-sm font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Notas das Provas
              </h3>
              <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                4 bimestres com 3 notas cada, prova final e média final.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ano letivo:</label>
              <input
                type="number"
                value={notasYear}
                onChange={(e) => setNotasYear(parseInt(e.target.value) || new Date().getFullYear())}
                className={`w-24 px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
                  ? 'bg-slate-700 border-slate-600 text-slate-200'
                  : 'bg-white border-slate-200 text-slate-700'
                }`}
              />
              <button
                onClick={handleDownloadNotasPdf}
                disabled={!disciplinaDaNota || alunos.length === 0 || downloadingPdf === 'notas'}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  !disciplinaDaNota || alunos.length === 0 || downloadingPdf === 'notas'
                    ? 'bg-slate-500 text-white cursor-not-allowed opacity-60'
                    : 'text-white bg-gradient-to-r from-[#E83A3C] to-[#C4292B] hover:shadow-lg hover:shadow-[#E83A3C]/25'
                }`}
                title="Baixar boletim de notas em PDF"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {downloadingPdf === 'notas' ? 'Gerando...' : 'Baixar PDF'}
              </button>
              <button
                onClick={handleBaixarNotasExcel}
                disabled={!disciplinaDaNota || alunos.length === 0 || baixandoNotasExcel}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  !disciplinaDaNota || alunos.length === 0 || baixandoNotasExcel
                    ? 'bg-slate-500 text-white cursor-not-allowed opacity-60'
                    : 'text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                }`}
                title="Excel com os alunos nas linhas e as notas já lançadas"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {baixandoNotasExcel ? 'Baixando...' : 'Baixar em Excel'}
              </button>
              {/* Abre o passo a passo, como na aba Planilha: quem nunca
                  baixou a planilha não sabe qual arquivo enviar. */}
              {canEdit && (
                <button
                  onClick={() => setMostrarComoImportarNotas(true)}
                  disabled={!disciplinaDaNota || alunos.length === 0 || importandoNotas}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    !disciplinaDaNota || alunos.length === 0 || importandoNotas
                      ? 'bg-slate-500 text-white cursor-not-allowed opacity-60'
                      : 'text-white bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {importandoNotas ? 'Importando...' : 'Importar planilha preenchida'}
                </button>
              )}
            </div>
          </div>

          {resultadoNotas && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              resultadoNotas.success
                ? darkMode
                  ? 'bg-[#13983D]/10 border border-[#13983D]/20 text-[#13983D]'
                  : 'bg-[#13983D]/5 border border-[#13983D]/20 text-[#13983D]'
                : darkMode
                  ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]'
                  : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20 text-[#E83A3C]'
            }`}>
              {resultadoNotas.success ? (
                <div>
                  <p className="font-medium">Importação realizada com sucesso!</p>
                  <p className="text-xs mt-1">
                    {resultadoNotas.rows_processed} alunos lidos · {resultadoNotas.notas} notas gravadas
                  </p>
                  {resultadoNotas.errors && resultadoNotas.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-[#F9B047]">Não entraram:</p>
                      {resultadoNotas.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-[#F9B047]">{err}</p>
                      ))}
                      {resultadoNotas.errors.length > 5 && (
                        <p className="text-xs text-[#F9B047]">...e mais {resultadoNotas.errors.length - 5} avisos</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p>{resultadoNotas.error || 'Erro ao importar planilha.'}</p>
              )}
            </div>
          )}

          {mostrarComoImportarNotas && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className={`w-full max-w-lg max-h-full overflow-y-auto rounded-2xl p-6 ${darkMode
                ? 'bg-slate-800 border border-slate-700'
                : 'bg-white shadow-xl'}`}>
                <h2 className={`text-xl font-semibold mb-1 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  Importar planilha de notas
                </h2>
                <p className={`text-sm mb-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Serve para lançar as notas da turma de uma vez, no computador,
                  sem digitar célula por célula. São três passos.
                </p>

                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">1</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Baixe a planilha desta disciplina
                      </p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Ela vem com os alunos nas linhas, os quatro bimestres nas
                        colunas e as notas de {notasYear} que já estão lançadas.
                        É este arquivo que você vai devolver.
                      </p>
                      <button
                        onClick={handleBaixarNotasExcel}
                        disabled={baixandoNotasExcel}
                        className={`mt-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white ${
                          baixandoNotasExcel ? 'bg-slate-500' : 'bg-[#0974BA] hover:bg-[#0974BA]/80'
                        }`}
                      >
                        {baixandoNotasExcel ? 'Baixando...' : 'Baixar em Excel'}
                      </button>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">2</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Digite as notas de 0 a 10
                      </p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <b>N1, N2, N3</b> são as avaliações e <b>R1, R2, R3</b> as
                        recuperações de cada uma. Pode usar vírgula (7,5). Célula
                        em branco fica como está — não apaga o que já foi lançado.
                        As médias se calculam sozinhas.
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Não mexa nos nomes, na matrícula, na disciplina nem no
                        cabeçalho: é por eles que o sistema reconhece cada aluno
                        e cada nota.
                      </p>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#13983D] text-white text-xs font-bold flex items-center justify-center">3</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Envie o arquivo salvo
                      </p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        O mesmo arquivo que você baixou, agora preenchido
                        (.xlsx). As notas da tela atualizam na hora.
                      </p>
                      <label className={`inline-flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer text-white ${
                        importandoNotas ? 'bg-slate-500 cursor-not-allowed' : 'bg-[#13983D] hover:bg-[#13983D]/80'
                      }`}>
                        {importandoNotas ? 'Importando...' : 'Escolher arquivo preenchido'}
                        <input
                          type="file"
                          accept=".xlsx"
                          onChange={(e) => { setMostrarComoImportarNotas(false); handleImportarNotas(e); }}
                          disabled={importandoNotas}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </li>
                </ol>

                <div className="flex justify-end mt-6 pt-4 border-t border-slate-700/10">
                  <button
                    onClick={() => setMostrarComoImportarNotas(false)}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium ${darkMode
                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}

          {ehPolivalente && (
            <div className="mb-4">
              <p className={`text-xs mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Polivalente: escolha a disciplina para lançar as notas. Quem
                define a lista é a coordenação, no cadastro da turma.
              </p>
              <div className="flex flex-wrap gap-2">
                {componentesPoli.componentes.map((c) => {
                  const ativo = String(componenteNota) === String(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setComponenteNota(String(c.id))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${ativo
                        ? 'text-white border-transparent shadow'
                        : darkMode
                          ? 'border-slate-600 text-slate-300 hover:border-slate-500'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                      style={ativo ? { background: 'linear-gradient(135deg, #0974BA, #2E4974)' } : {}}
                    >
                      {c.subject_name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!disciplinaDaNota ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">📚</div>
              <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                Selecione uma disciplina para lançar as notas.
              </p>
            </div>
          ) : alunos.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">👥</div>
              <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                Nenhum aluno cadastrado nesta turma.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700' : 'bg-slate-100'}>
                    <th rowSpan={2} className={`text-left px-3 py-2 sticky left-0 z-10 border ${darkMode ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      Aluno
                    </th>
                    {[1, 2, 3, 4].map(b => (
                      <th key={b} colSpan={7} className={`text-center px-2 py-2 border ${darkMode ? 'text-slate-300 border-slate-600' : 'text-slate-600 border-slate-200'}`}>
                        {b}º Bimestre
                      </th>
                    ))}
                    <th rowSpan={2} className={`text-center px-2 py-2 border ${darkMode ? 'text-slate-300 border-slate-600' : 'text-slate-600 border-slate-200'}`}>
                      Prova Final
                    </th>
                    <th rowSpan={2} className={`text-center px-2 py-2 border ${darkMode ? 'text-slate-300 border-slate-600' : 'text-slate-600 border-slate-200'}`}>
                      Média Final
                    </th>
                  </tr>
                  <tr className={darkMode ? 'bg-slate-700/70' : 'bg-slate-50'}>
                    {[1, 2, 3, 4].map(b => (
                      <React.Fragment key={`sub-${b}`}>
                        <th className={`text-center px-1 py-1 text-xs font-medium border ${darkMode ? 'text-slate-400 border-slate-600' : 'text-slate-500 border-slate-200'}`}>N1</th>
                        <th className={`text-center px-1 py-1 text-xs font-medium border ${darkMode ? 'text-amber-300/80 border-slate-600' : 'text-amber-600 border-slate-200'}`} title="Recuperação da N1">R1</th>
                        <th className={`text-center px-1 py-1 text-xs font-medium border ${darkMode ? 'text-slate-400 border-slate-600' : 'text-slate-500 border-slate-200'}`}>N2</th>
                        <th className={`text-center px-1 py-1 text-xs font-medium border ${darkMode ? 'text-amber-300/80 border-slate-600' : 'text-amber-600 border-slate-200'}`} title="Recuperação da N2">R2</th>
                        <th className={`text-center px-1 py-1 text-xs font-medium border ${darkMode ? 'text-slate-400 border-slate-600' : 'text-slate-500 border-slate-200'}`}>N3</th>
                        <th className={`text-center px-1 py-1 text-xs font-medium border ${darkMode ? 'text-amber-300/80 border-slate-600' : 'text-amber-600 border-slate-200'}`} title="Recuperação da N3">R3</th>
                        <th className={`text-center px-1 py-1 text-xs font-medium border ${darkMode ? 'text-slate-400 border-slate-600' : 'text-slate-500 border-slate-200'}`}>Média</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {alunos.map(aluno => {
                    const studentId = aluno.id_student || aluno.id;
                    const s = getStudentNotas(studentId);
                    const bimestres = [
                      { key: 'b1', recupKey: 'b1r', notas: s.b1, recups: s.b1r },
                      { key: 'b2', recupKey: 'b2r', notas: s.b2, recups: s.b2r },
                      { key: 'b3', recupKey: 'b3r', notas: s.b3, recups: s.b3r },
                      { key: 'b4', recupKey: 'b4r', notas: s.b4, recups: s.b4r },
                    ];
                    const mediaFinal = calcMediaFinal(s);
                    return (
                      <tr key={studentId} className={darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}>
                        {/* Fundo opaco e z-index: a coluna fica presa à
                            esquerda e as notas rolam por baixo dela — com o
                            fundo translúcido os números apareciam através do
                            nome. No celular o nome quebra em linhas numa
                            coluna estreita, para sobrar tela para as notas. */}
                        <td className={`px-3 py-2 font-medium sticky left-0 z-[1] border ${darkMode ? 'bg-slate-800 text-slate-200 border-slate-600' : 'bg-white text-slate-700 border-slate-200'}`}>
                          <div className="flex items-center gap-2 w-32 sm:w-auto whitespace-normal sm:whitespace-nowrap text-xs sm:text-sm leading-tight">
                            <span>{aluno.student_name}</span>
                            {notasSaving[studentId] && (
                              <span className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>salvando…</span>
                            )}
                          </div>
                          {notasErro[studentId] && (
                            <div className="text-[10px] text-[#E83A3C] max-w-[220px] whitespace-normal mt-0.5">
                              {notasErro[studentId]}
                            </div>
                          )}
                        </td>
                        {bimestres.map(({ key, recupKey, notas, recups }) => {
                          const media = calcMediaBimestre(notas, recups);
                          const inputBase = `w-16 px-2 py-1 rounded text-center text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`;
                          // Nota gravada antes do limite valer: fica marcada
                          // até alguém corrigir, porque é ela que puxa a média
                          // da turma e do ano para fora da escala.
                          const marcaForaDaEscala = (v) =>
                            notaForaDaEscala(v) ? ' ring-2 ring-[#E83A3C] border-[#E83A3C]' : '';
                          return (
                            <React.Fragment key={key}>
                              {[0, 1, 2].map(idx => (
                                <React.Fragment key={idx}>
                                  <td className={`px-1 py-1 text-center border ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                    <input
                                      type="number" min="0" max="10" step="0.1"
                                      disabled={!canEdit}
                                      value={notas[idx]}
                                      onChange={(e) => handleNotaChange(studentId, key, idx, e.target.value)}
                                      title={notaForaDaEscala(notas[idx]) ? 'Nota fora da escala de 0 a 10 — corrija para poder salvar esta linha' : undefined}
                                      className={`${inputBase} ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'}${marcaForaDaEscala(notas[idx])}`}
                                    />
                                  </td>
                                  <td className={`px-1 py-1 text-center border ${darkMode ? 'border-slate-600 bg-amber-500/5' : 'border-slate-200 bg-amber-50/60'}`}>
                                    <input
                                      type="number" min="0" max="10" step="0.1"
                                      disabled={!canEdit}
                                      value={recups[idx]}
                                      title={`Recuperação da N${idx + 1}`}
                                      onChange={(e) => handleRecupChange(studentId, recupKey, idx, e.target.value)}
                                      className={`${inputBase} ${darkMode ? 'bg-slate-700/60 border-amber-500/30 text-amber-200 placeholder-slate-500' : 'bg-amber-50 border-amber-200 text-amber-800 placeholder-amber-300'}${marcaForaDaEscala(recups[idx])}`}
                                    />
                                  </td>
                                </React.Fragment>
                              ))}
                              <td className={`px-2 py-1 text-center font-semibold border ${notaColor(media)} ${darkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                {formatNota(media)}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td className={`px-1 py-1 text-center border ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            disabled={!canEdit}
                            value={s.final}
                            onChange={(e) => handleProvaFinalChange(studentId, e.target.value)}
                            title={notaForaDaEscala(s.final) ? 'Nota fora da escala de 0 a 10 — corrija para poder salvar esta linha' : undefined}
                            className={`w-16 px-2 py-1 rounded text-center text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
                              ? 'bg-slate-700 border-slate-600 text-slate-200'
                              : 'bg-white border-slate-200 text-slate-700'
                            } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}${notaForaDaEscala(s.final) ? ' ring-2 ring-[#E83A3C] border-[#E83A3C]' : ''}`}
                          />
                        </td>
                        <td className={`px-2 py-1 text-center font-bold border ${notaColor(mediaFinal)} ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-100 border-slate-200'}`}>
                          {formatNota(mediaFinal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-600/30">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#13983D]" />
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>≥ 7,0</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#F9B047]" />
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>5,0 a 6,9</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#E83A3C]" />
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>&lt; 5,0</span>
                </div>
                <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  R1/R2/R3 = recuperação · a média usa a maior entre cada nota e a sua recuperação
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <ExportarRegistroAulasModal
        aberto={exportandoRegistro}
        subjects={subjects}
        disciplinaInicial={selectedSubject ? String(selectedSubject) : ''}
        anoLetivo={turma?.school_year || calendarYear}
        mesInicial={`${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}`}
        gerando={downloadingPdf === 'registro'}
        onExportar={handleExportarRegistro}
        onFechar={() => setExportandoRegistro(false)}
      />
    </>
  );
}

export default DiarioTurma;
